import { Component, OnInit, OnDestroy, ViewChild, NgZone, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { HttpClientModule, HttpEventType } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SatelliteProductDto, SatelliteProductService } from '@6grain/masofa-monolith-api-client/common';
import {
  CustomGetSatelliteProductsRequest,
  CustomGetSatelliteProductsResponse,
} from '@6grain/masofa-monolith-api-client/common';
import { SatelliteProductsService } from '@app/services/satellite-products.service';
import { Observable, catchError, forkJoin, of, map, switchMap } from 'rxjs';
import { RegionDictionaryService, RegionDictionaryNode } from '@app/services/region-dictionary.service';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { GisPolygon } from '@app/components/openstreetmap/interfaces/gis-component.interfaces';
import { OpenStreetMapComponent } from '@app/components/openstreetmap/openstreetmap.component';
import {
  MapControllerInput,
  MapInitOptions,
  MapClickEvent,
  MapMoveEvent,
} from '@app/components/openstreetmap/interfaces';
import { DatePickerComponent } from '@app/components/date-picker/date-picker.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ThemeService } from '@app/services/theme.service';
import { CheckboxComponent } from '@app/components/checkbox/checkbox.component';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import { DataSourceSwitcherComponent, DataSourceTab } from '@app/components/data-source-switcher/data-source-switcher.component';
import { RequestData } from '@app/pages/Ui-Kit/requests-table/requests-table.component';
import { UploadRequestModalComponent } from '@app/components/upload-request-modal/upload-request-modal.component';
import { GeoImageUploadModalComponent } from './uploaded-geo-images/geo-image-upload-modal/geo-image-upload-modal.component';
import { UploadedGeoImagesComponent } from './uploaded-geo-images/uploaded-geo-images.component';
import { UploadedGeoImagesMapComponent } from './uploaded-geo-images/uploaded-geo-images-map/uploaded-geo-images-map.component';
import { GeoImageFiltersComponent } from './uploaded-geo-images/geo-image-filters/geo-image-filters.component';
import { UploadedGeoImagesStateService } from './uploaded-geo-images/services/uploaded-geo-images-state.service';
import { environment } from '@root/src/environments/environment';
import {
  getMaxCloudCoverPayloadValue,
  normalizeCloudCover,
  normalizeMaxCloudCoverInput,
  readTerritoryCloudCover,
} from '@app/utils/cloud-cover.util';
import { SatelliteTaskStatusService, GetSatelliteTaskStatusRequest } from './services/satellite-task-status.service';
import { DownloadProgressComponent } from '@components/ui/download-progress/download-progress.component';
import { Subject, takeUntil } from 'rxjs';
import { MsfTreeSelect } from '@components/tree-select/tree-select';
import { ElectronicFieldPolygonService } from '@app/pages/satellite-images/services/electronic-field-polygon.service';
import { OwnerTagBinding, TagDictionaryService } from '@app/services/tag-dictionary.service';
import { SATELLITE_PRODUCT_TAG_OWNER_TYPE } from '@app/utils/tag-owner-types';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {StatusTag} from '@components/ui/status-tag/status-tag';
import { BackButton } from '@components/ui/back-button/back-button';
import { ExternalScenesViewComponent } from './components/external-scenes-view/external-scenes-view.component';
import { ExternalScenesStateService } from './external-scenes/external-scenes-state.service';
import { readMapBounds } from './external-scenes/external-scenes-bounds.util';
import { ExternalSceneProviderFilter } from './external-scenes/external-scenes.models';
import { DrawnShapeLike, drawnShapeToGeoJson, watchDrawnShapeRemoval } from './external-scenes/external-scenes-map.util';
import { fileIdOrUndefined, hasFileId } from '@app/shared/helpers/file-id.helper';
import { MapRequestContextMenuComponent } from './components/map-request-context-menu/map-request-context-menu.component';
import { MapRequestFlowService } from './services/map-request-flow.service';
import {
  SatelliteNamePipe,
  SatelliteTypeClassPipe,
  SatelliteTypeShortPipe,
  TaskDateTimePipe,
  TaskStatusClassPipe,
} from './satellite-images-display.pipes';

interface SatelliteTaskStatus {
  id: string;
  productId: string;
  productName?: string;
  satelliteType: number;
  status: number;
  statusName: string;
  createAt: string;
  originDate?: string;
  progressPercent: number;
  regionName?: string;
  indexStatus?: {
    isTiffComplete: boolean;
    isDbComplete: boolean;
    indexDetails: Array<{
      indexName: string;
      tiffGenerated: boolean;
      dbLoaded: boolean;
    }>;
  };
  lastUpdated?: string;
  errorMessage?: string;
}

interface SatelliteImage{
  id: number;
  name: string;
  mission: string;
  instrument: string;
  createAt: Date;
  originDate: Date;
  size: number;
  unit: string;
  areas: string[];
  center: any,
  type: string;
  tag: string;
  thumbnail: string;
  apiProductId?: string;
  productId?: string;
  /** Id of the stored archive; undefined until the archive has been downloaded. */
  mediadataPath?: string;
  sentinelInspireMetadataId?: string;
  tags?: Array<{ id: string; name: string }>; // Добавляем свойство tags
  isPublic?: boolean; // Добавляем поле isPublic для публикации
  cloudCover: number | null;
  /** Cloud cover inside the country border; null when the backend has not measured it. */
  territoryCloudCover: number | null;
};

@Component({
  selector: 'app-satellite-images',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    TranslateModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    OpenStreetMapComponent,
    DatePickerComponent,
    TranslatePipe,
    CheckboxComponent,
    DataSourceSwitcherComponent,
    UploadRequestModalComponent,
    MapRequestContextMenuComponent,
    GeoImageUploadModalComponent,
    UploadedGeoImagesComponent,
    UploadedGeoImagesMapComponent,
    GeoImageFiltersComponent,
    TagSelectorComponent,
    DownloadProgressComponent,
    MsfTreeSelect,
    PrimaryButton,
    SecondaryButton,
    StatusTag,
    BackButton,
    ExternalScenesViewComponent,
    SatelliteNamePipe,
    SatelliteTypeClassPipe,
    SatelliteTypeShortPipe,
    TaskDateTimePipe,
    TaskStatusClassPipe,
  ],
  providers: [MessageService, MapRequestFlowService, UploadedGeoImagesStateService, ExternalScenesStateService],
  templateUrl: './satellite-images.component.html',
  styleUrl: './satellite-images.component.scss',
})
export class SatelliteImagesComponent implements OnInit, OnDestroy {
  @ViewChild('openstreetmapComponent') openstreetmapComponent!: OpenStreetMapComponent;
  @ViewChild(UploadedGeoImagesMapComponent) uploadedGeoImagesMap?: UploadedGeoImagesMapComponent;

  // Map Controller & Options (как в ГИС)
  mapController$ = new Subject<MapControllerInput>();
  /** Last shape finished with the map's drawing toolbar; used as the search area on the `download` tab. */
  private lastDrawnShape: DrawnShapeLike | null = null;

  mapOptions: MapInitOptions = {
    height: '100%',
    center: [environment.map.lat, environment.map.lng],
    zoom: 8,
    showControls: true,
    minimalControls: false, // Показываем все элементы управления карты
    drawingMode: false,
    showDrawingControls: true, // Элементы управления рисованием уже есть в карте
    baseMap: 'osm',
    showRegionNames: true, // Показывать названия регионов (как в ГИС)
    disableBounds: false,
  };

  mapCenter: [number, number] = [environment.map.lat, environment.map.lng];
  mapZoom: number = 8;
  mapMarkers: any[] = [];
  private destroy$ = new Subject<void>(); // Для подписок на события карты
  mapOpacity: number = 65; // прозрачность OSM слоя по умолчанию 65%
  indexOpacity: number = 80; // прозрачность слоя индекса по умолчанию 80%

  // Выбор индексов теперь через встроенный компонент карты (DatePicker + MsfSelect в левом нижнем углу)

  // Лоадеры
  isLoadingProducts: boolean = false; // лоадер для загрузки продуктов

  selectedLayer: string = 'NDVI';
  isLayerDropdownOpen: boolean = false;
  // Две независимые выпадашки: Индексы и Слои
  isIndexMenuOpen: boolean = false;
  isLayerMenuOpen: boolean = false;
  // Изначально только "Не выбран", полный список заполнится после выбора снимка
  indexPresets: Array<{ label: string; icon: string; key: string; isSeparator?: boolean }> = [
    { label: 'NOT_INDEX_SELECTED', icon: 'assets/icons/main/light/Group 6.svg', key: 'none', isSeparator: true },
  ];
  layerPresets: Array<{ label: string; icon: string; mission: string; key: string }> = [
    { label: 'True color', icon: 'assets/icons/main/light/Group 6.svg', mission: 'SENTINEL-1', key: 'truecolor' },
    { label: 'False color', icon: 'assets/icons/main/light/Group 6.svg', mission: 'SENTINEL-1', key: 'falsecolor' },
    { label: 'SWIR', icon: 'assets/icons/main/light/Group 6.svg', mission: 'SENTINEL-1', key: 'swir' },
  ];
  selectedIndexPreset: { label: string; icon: string; key: string; isSeparator?: boolean } | null = null;
  selectedLayerPreset: { label: string; icon: string; mission: string; key: string } | null = null;
  selectedDataSource: string = 'all';
  satelliteOptions = [
    { labelKey: 'ALL_SATELLITES', value: 'all' },
    { labelKey: 'SATELLITE_SENTINEL', value: 'sentinel' },
    { labelKey: 'SATELLITE_LANDSAT', value: 'landsat' },
  ];
  activeTab: DataSourceTab = 'satellites';
  /** The uploaded-images map is created on first visit and kept (hidden) afterwards, so its viewport survives tab switches. */
  uploadedTabVisited = false;
  showGeoImageUploadModal = false;

  layerOptions = [
    { label: 'NDVI', value: 'NDVI' },
    { label: 'NDMI', value: 'NDMI' },
    { label: 'GNDVI', value: 'GNDVI' },
    { label: 'OSAVI', value: 'OSAVI' },
    { label: 'MNDWI', value: 'MNDWI' },
    { label: 'NDWI', value: 'NDWI' },
    { label: 'EVI', value: 'EVI' },
    { label: 'ORVI', value: 'ORVI' },
  ];

  satelliteImages: SatelliteImage[] = [];

  selectedProductId: string | null = null; // ID активного продукта
  availableIndices: string[] = []; // Доступные индексы для активного продукта
  private productLayerMap: { [indexKey: string]: string } = {}; // Карта индекс -> конкретный layerName для выбранного продукта
  selectedProductIndex: { [productId: string]: string } = {}; // Выбранный индекс для каждого продукта
  productIndexDropdownOpen: { [productId: string]: boolean } = {}; // Состояние dropdown для каждого продукта
  // Индексы по продукту (получаем через TileLayer/GetByQuery по дате продукта, geoServerName=INDEX_YYYYMMDD)
  private productAvailableIndices: { [productId: string]: string[] } = {};
  private currentSatellitePolygonLeafletCoords: [number, number][] | null = null; // [lat,lng] для клиппинга тайла
  private clippedTileLayer: any = null; // Локальный clipped-слой только для satellite-images (не влияет на другие страницы)

  // Runtime patch для indexManager на ЭТОЙ странице:
  // если есть контур снимка — глобальный индексный слой карты будет рисоваться только внутри контура.
  private originalIndexManagerAddIndexTileLayer: any = null;
  private isIndexManagerPatched = false;
  showExportModal: boolean = false;

  // Map right-click request flow (issue #862): all state and rules live in the page-scoped flow.
  private readonly zone = inject(NgZone);
  readonly mapRequest = inject(MapRequestFlowService);

  // По умолчанию: период за последнюю неделю (как в GIS)
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  selectedRegion: string = ''; // ID района (level 3)
  selectedParentRegion: string = ''; // ID области (level 2)
  selectedRegionId: string | null = null; // binding for msf-tree-select
  selectedMaxCloudCover: number = 100;
  selectedTag: string = '';
  showRegionDropdown: boolean = false;
  regionSearch: string = ''; // Переименовано из regionSearchText для соответствия upload-images
  expandedRegionIds: Set<string> = new Set<string>(); // Переименовано из expandedRegions
  selectedRegionLabel: string = '';
  selectedRegionBoundary: [number, number][] | null = null;
  showDataSourceDropdown: boolean = false;
  showTagDropdown: boolean = false;
  tagSearchText: string = '';

  // Дерево регионов как в upload-images
  regionTree: RegionDictionaryNode[] = [];
  filteredRegionTree: RegionDictionaryNode[] = [];
  private regionIndex = new Map<string, RegionDictionaryNode>();
  // Кэш границ регионов (как в GIS/upload-images) — чтобы dropdown/контуры были мгновенными
  private regionBoundaryCache = new Map<string, [number, number][]>();
  private regionBoundaryHighlightId: string | null = null;

  // Кэш регионов по ID для быстрого поиска названий
  regionsById: Map<string, any> = new Map();

  interfaceRegionOption?: never; // placeholder to keep file-local interface semantics minimal
  regionOptions: Array<{
    label: string;
    value: string;
    id?: string;
    names?: { [key: string]: string };
    children?: Array<any>;
    regionMapId?: string;
    level?: number | string;
    isHeader?: boolean;
    lat?: number;
    lng?: number;
    hasChildren?: boolean;
  }> = [];
  tagOptions: Array<{ label: string; value: string }> = [];

  originDateOfLastSatelliteProduct?: Date;

  // Пагинация
  currentPage: number = 1;
  pageSize: number = 5;
  totalRecords: number = 0;
  totalPages: number = 0;

  // Кеш загруженных продуктов по зонам карты (как «пузырьки» в upload-images)
  private loadedZones: Array<{
    bounds: { north: number; south: number; east: number; west: number };
    productIds: Set<string>;
    loadedAt: Date;
  }> = [];
  private cachedProducts: Map<string, any> = new Map();
  // Обновление данных
  lastUpdatedAt: Date | null = null;
  showUpdatedBanner: boolean = false;

  // Фильтры
  filtersVisible: boolean = true;
  private langSub?: Subscription;
  // Тип карты: 'satellite' | 'osm'
  selectedMapType: 'satellite' | 'osm' = 'satellite';

  // requestsData replaced later when API endpoint will be provided
  requestsData: RequestData[] = [];

  // Данные для статуса задач
  taskStatusData: SatelliteTaskStatus[] = [];
  taskStatusLoading = false;
  taskStatusTotal = 0;

  // Данные для тегов
  showTagModal = false;
  selectedImageForTags: any = null;
  imageTags: OwnerTagBinding[] = [];
  tagLoading = false;
  // Выбранные ID тегов для tag-selector в модалке
  editingTagIds: string[] = [];

  // Модалка просмотра информации о снимке
  showInfoModal = false;
  infoModalLoading = false;
  infoModalImage: any = null; // базовые данные из списка
  infoModalProduct: any = null; // полные данные продукта
  infoModalSentinelMetadata: any = null; // метаданные Sentinel2
  infoModalInspireMetadata: any = null; // Inspire-метаданные
  infoModalPreviewUrl: string | null = null;
  infoModalTagNames: string[] = [];

  // Модалка скачивания с прогресс-баром
  showDownloadModal = false;
  downloadProgress = 0;
  downloadingFileName = '';
  private downloadCancellation$ = new Subject<void>();

  // Данные для редактирования снимка
  editingImageIsPublic = false;
  savingImageChanges = false;

  // Активный продукт для отображения на карте
  activeProductId: string | null = null;

  // Текущие границы карты (для загрузки продуктов по видимой области)
  currentMapBounds: { north: number; south: number; east: number; west: number } | null = null;

  // Флаг, чтобы не перерисовывать границы регионов многократно
  private regionBoundariesLoaded = false;

  constructor(
    private themeService: ThemeService,
    private location: Location,
    private translate: TranslateService,
    private regionDictionaryService: RegionDictionaryService,
    public regionManagementManager: RegionManagerService,
    private polygonService: ElectronicFieldPolygonService,
    private satelliteProductService: SatelliteProductService,
    // Оставляем старый сервис для методов, которых нет в API клиенте
    private satelliteProducts: SatelliteProductsService,
    private satelliteTaskStatusService: SatelliteTaskStatusService,
    private tagService: TagDictionaryService,
    private messageService: MessageService,
    private uploadedGeoImages: UploadedGeoImagesStateService,
    private externalScenes: ExternalScenesStateService,
  ) {}

  ngOnInit(): void {
    this.mapRequest.connect(() => this.isMapDrawingActive());

    this.loadMapData();

    // Добавляем глобальный обработчик кликов для закрытия меню
    this.setupGlobalClickHandler();

    // Загрузить регионы из справочника
    this.loadRegions();

    // Загрузить теги для фильтра
    this.loadTagsForFilter();

    // Дефолтный период: неделя назад -> сегодня
    this.resetDateRangeToDefault();

    // Пересобираем локализованные подписи при смене языка (без повторного запроса)
    this.langSub = this.translate.onLangChange.subscribe(() => {
      // Перезагружаем регионы при смене языка для получения локализованных названий
      this.loadRegions();
      this.loadTagsForFilter();

      // Перерисовываем границы регионов с учётом нового языка
      this.regionBoundariesLoaded = false;
      if (this.openstreetmapComponent && this.openstreetmapComponent.isMapInitialized) {
        try {
          // TODO: Заменить на метод через mapController$ если будет нужен clearRegionBoundaries
          // this.mapController$.next({ clearPolygons: true });
        } catch {}
      }
      this.loadRegionBoundaries();
    });

    // Загружаем список продуктов при первой инициализации
    // Список будет зависеть только от фильтров, а не от границ карты
    setTimeout(() => {
      this.loadProductsList();
    }, 1000);
    this.completeInit();

    // Глобальный индексный слой управляется самим `OpenStreetMapComponent`
    // (DatePicker + Select внизу слева). Здесь не вмешиваемся, чтобы работало как в ГИСах.

    // Подписываемся на события карты
    this.subscribeToMapEvents();
  }

  /**
   * Подписывается на события карты OpenStreetMapComponent
   */
  private subscribeToMapEvents(): void {
    // Подписываемся на события после инициализации карты
    setTimeout(() => {
      if (this.openstreetmapComponent) {
        // Подписка на клики по карте
        if (this.openstreetmapComponent.mapClick$) {
          this.openstreetmapComponent.mapClick$.pipe(takeUntil(this.destroy$)).subscribe((event: MapClickEvent) => {
            this.onMapClick(event);
          });
        }

        // Right click: offer the processing-request action for the clicked point
        if (this.openstreetmapComponent.mapRightClick$) {
          this.openstreetmapComponent.mapRightClick$.pipe(takeUntil(this.destroy$)).subscribe((event: MapClickEvent) => {
            this.zone.run(() => this.mapRequest.onRightClick(event));
          });
        }

        // Подписка на перемещение карты
        if (this.openstreetmapComponent.mapMove$) {
          this.openstreetmapComponent.mapMove$.pipe(takeUntil(this.destroy$)).subscribe((event: MapMoveEvent) => {
            this.onMapMove(event);
          });
        }

        // Подписка на изменение зума
        if (this.openstreetmapComponent.mapZoomChange$) {
          this.openstreetmapComponent.mapZoomChange$.pipe(takeUntil(this.destroy$)).subscribe((zoom: number) => {
            this.onMapZoomChange(zoom);
          });
        }
      }
    }, 500); // Ждем инициализации карты
  }

  ngOnDestroy(): void {
    this.restoreIndexManagerIfPatched();
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
    // Удаляем глобальный обработчик кликов
    document.removeEventListener('click', this.globalClickHandler);
    // Очищаем подписку на скачивание
    this.downloadCancellation$.next();
    this.downloadCancellation$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private restoreIndexManagerIfPatched(): void {
    try {
      const indexManager = this.openstreetmapComponent?.indexManager as any;
      if (this.isIndexManagerPatched && indexManager && this.originalIndexManagerAddIndexTileLayer) {
        indexManager.addIndexTileLayer = this.originalIndexManagerAddIndexTileLayer;
      }
    } catch {}
    this.isIndexManagerPatched = false;
    this.originalIndexManagerAddIndexTileLayer = null;
  }

  /**
   * Обработчик глобальных кликов для закрытия выпадающих меню
   */
  private globalClickHandler = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    // Проверяем клик вне меню индексов
    const indexMenu = target.closest('.index-menu');
    if (!indexMenu && this.isIndexMenuOpen) {
      this.isIndexMenuOpen = false;
    }

    // Проверяем клик вне дропдауна индексов в модальном окне

    // Проверяем клик вне меню слоев
    const layerMenu = target.closest('.layer-menu');
    if (!layerMenu && this.isLayerMenuOpen) {
      this.isLayerMenuOpen = false;
    }

    // Проверяем клик вне выпадашки источника данных
    const dataSourceSelector = target.closest('.data-source-selector');
    if (!dataSourceSelector && this.showDataSourceDropdown) {
      this.showDataSourceDropdown = false;
    }

    // Проверяем клик вне выпадашки тегов
    const tagSelector = target.closest('.tag-selector');
    if (!tagSelector && this.showTagDropdown) {
      this.showTagDropdown = false;
    }
  };

  /**
   * Настройка глобального обработчика кликов
   */
  private setupGlobalClickHandler(): void {
    // Используем setTimeout чтобы обработчик не сработал на текущем клике
    setTimeout(() => {
      document.addEventListener('click', this.globalClickHandler);
    }, 0);
  }

  loadTaskStatus(): void {
    this.taskStatusLoading = true;
    const offset = (this.currentPage - 1) * this.pageSize;
    // Безопасное преобразование дат (без учета часового пояса, чтобы избежать скачков даты)
    const startDateStr = this.selectedStartDate
      ? this.formatDateForQuery(
          this.selectedStartDate instanceof Date ? this.selectedStartDate : new Date(this.selectedStartDate),
        )
      : undefined;
    const endDateStr = this.selectedEndDate
      ? this.formatDateForQuery(
          this.selectedEndDate instanceof Date ? this.selectedEndDate : new Date(this.selectedEndDate),
        )
      : undefined;

    const request: GetSatelliteTaskStatusRequest = {
      take: this.pageSize,
      offset: offset,
      sortBy: 'CreateAt',
      sort: -1,
      startDate: startDateStr,
      endDate: endDateStr,
    };

    this.satelliteTaskStatusService.getTaskStatus(request).subscribe({
      next: (response) => {
        this.taskStatusData = response.data || [];
        this.taskStatusTotal = response.total || 0;
        // Вычисляем totalPages для задач
        this.totalPages = Math.ceil(this.taskStatusTotal / this.pageSize);
        this.taskStatusLoading = false;
      },
      error: (error) => {
        console.error('Error loading task status:', error);
        this.taskStatusData = [];
        this.taskStatusTotal = 0;
        this.totalPages = 0;
        this.taskStatusLoading = false;
      },
    });
  }

  /**
   * Обновляет список снимков после изменения тегов
   */
  private refreshProductsList(): void {
    this.clearProductsCache();
    // Сбрасываем на первую страницу при обновлении
    this.currentPage = 1;
    this.loadProductsList();
  }

  private loadProductsList(): void {
    // The `download` tab shows external provider scenes only; the local catalog is neither read nor cleared there.
    if (this.activeTab === 'download') {
      return;
    }
    this.isLoadingProducts = true; // Включаем лоадер
    this.satelliteImages = []; // Очищаем список снимков, чтобы лоадер был вместо них

    const offset = (this.currentPage - 1) * this.pageSize;

    // Безопасное преобразование дат (без учета часового пояса, чтобы избежать скачков даты)
    const startDateStr = this.selectedStartDate
      ? this.formatDateForQuery(
          this.selectedStartDate instanceof Date ? this.selectedStartDate : new Date(this.selectedStartDate),
        )
      : undefined;

    const endDateStr = this.selectedEndDate
      ? this.formatDateForQuery(
          this.selectedEndDate instanceof Date ? this.selectedEndDate : new Date(this.selectedEndDate),
        )
      : undefined;

    // Маппинг источника данных на тип спутника
    let satelliteType: number | undefined;
    switch (this.selectedDataSource) {
      case 'sentinel':
        satelliteType = 1; // Sentinel2
        break;
      case 'landsat':
        satelliteType = 2; // Landsat
        break;
      case 'all':
      default:
        satelliteType = undefined; // Все типы
        break;
    }

    // Определяем, какой регион использовать для фильтрации
    // Если выбран район (level 3) - используем regionId
    // Если выбран только родительский регион (level 2) - нужно использовать parentRegionId
    // API клиент не поддерживает parentRegionId, поэтому используем старый сервис для этого случая
    const useParentRegionFilter = !this.selectedRegion && this.selectedParentRegion;

    // Если выбран только родительский регион (level 2), используем старый сервис с parentRegionId
    const maxCloudCover = getMaxCloudCoverPayloadValue(this.selectedMaxCloudCover);
    if (useParentRegionFilter) {
      const oldRequest: any = {
        offset: offset,
        take: this.pageSize,
        sortBy: 'CreateAt',
        sort: -1,
        parentRegionId: this.selectedParentRegion, // Используем parentRegionId через старый сервис
        satelliteType: satelliteType,
        startDate: startDateStr,
        endDate: endDateStr,
        tagIds: this.selectedTag ? [this.selectedTag] : undefined,
      };

      if (maxCloudCover !== undefined) {
        oldRequest.maxCloudCover = maxCloudCover;
      }

      // Используем старый сервис для поддержки parentRegionId
      this.satelliteProducts.customGet(oldRequest).subscribe({
        next: (response: any) => {
          this.isLoadingProducts = false;

          const productsArray = response?.data || [];
          this.totalRecords = response?.total || 0;
          if (response?.originDateOfLastSatelliteProduct != null)
            this.originDateOfLastSatelliteProduct = new Date(response.originDateOfLastSatelliteProduct);
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);

          if(this.totalRecords == 0 && this.originDateOfLastSatelliteProduct)
          {
            this.resetDateRangeToDefault();
            this.loadProductsList();
          }

          this.lastUpdatedAt = this.originDateOfLastSatelliteProduct ?? null;
          this.showUpdatedBanner = true;

          this.satelliteImages = (productsArray || []).map((product: any, index: number) => {
            const apiId = product['id'] || product['Id'];
            const productIdForSentinel = product['productId'] || product['ProductId'];
            const previewImagePathFromProduct = product['previewImagePath'] || product['PreviewImagePath'];
            const bounds = product['bounds'];
            const originDate = new Date(product['originDate']);

            // Обработка regionLevel3Ids - проверяем новое и старое имя поля, а также регистр
            const regionLevel3Ids =
              product['regionLevel3Ids'] ||
              product['RegionLevel3Ids'] ||
              product['regionLevel2Ids'] ||
              product['RegionLevel2Ids'] ||
              [];
            const areas = Array.isArray(regionLevel3Ids) ? regionLevel3Ids.map((id: any) => String(id)) : [];

            // Логируем для отладки, если области пустые, но продукт есть
            if (areas.length === 0 && apiId) {
              console.warn('⚠️ Продукт без областей:', apiId, 'regionLevel3Ids:', regionLevel3Ids);
            }

            return {
              id: offset + index + 1,
              name: product['name'] || product['fileName'] || 'Unknown',
              mission: product['mission'] || 'SENTINEL-2',
              instrument: product['instrument'] || 'MSI',
              originDate: originDate,
              createAt: new Date(product['createAt']),
              size: product['sizeMb'] || 0,
              unit: 'MB',
              areas: areas, // Строковые ID регионов уровня 3 (районы)
              type: 'ndvi',
              tag: 'vegetation',
              thumbnail: previewImagePathFromProduct
                ? this.getFileUrl(previewImagePathFromProduct) || 'assets/icons/main/light/Group 6.svg'
                : 'assets/icons/main/light/Group 6.svg',
              apiProductId: apiId,
              productId: productIdForSentinel,
              sentinelInspireMetadataId: product['sentinelInspireMetadataId'] || product['SentinelInspireMetadataId'],
              previewImagePath: previewImagePathFromProduct,
              mediadataPath: fileIdOrUndefined(product['mediadataPath'] || product['MediadataPath']),
              tags: product['tags'] || [],
              isPublic: product['isPublic'] || false,
              bounds: bounds,
              center: product['center'],
              polygon: product['polygonJson'] || product['polygon'],
              cloudCover: normalizeCloudCover(product['cloudCover']),
              territoryCloudCover: readTerritoryCloudCover(product),
            } as SatelliteImage;
          });
        },
        error: (error) => {
          console.error('❌ Ошибка загрузки продуктов (old service):', error);
          this.isLoadingProducts = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('ERROR'),
            detail: this.translate.instant('ERROR_LOADING_IMAGES'),
          });
        },
      });

      return; // Выходим, так как использовали старый сервис
    }

    // УБРАНО: зависимость от границ карты - список должен зависеть только от фильтров
    // Используем API клиент из пакета 6grain для оптимизации

    const request: CustomGetSatelliteProductsRequest = {
      offset: offset,
      take: this.pageSize, // Добавляем take для пагинации на сервере
      sortBy: 'CreateAt',
      sort: -1,
      regionId: this.selectedRegion || undefined,
      // parentRegionId не поддерживается в CustomGetSatelliteProductsRequest из API клиента
      // Если выбран только parentRegion, нужно использовать старый сервис (выше)
      satelliteType: satelliteType as any,
      startDate: startDateStr,
      endDate: endDateStr,
      tagIds: this.selectedTag ? [this.selectedTag] : undefined,
    };

    if (maxCloudCover !== undefined) {
      request.maxCloudCover = maxCloudCover;
    }

    this.satelliteProductService.commonSatelliteProductCustomGetPost(request).subscribe({
      next: (response: CustomGetSatelliteProductsResponse) => {
        this.isLoadingProducts = false; // Выключаем лоадер

        const productsArray = response?.data || [];
        this.totalRecords = response?.total || 0;
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
        if (response?.originDateOfLastSatelliteProduct != null)
            this.originDateOfLastSatelliteProduct = new Date(response.originDateOfLastSatelliteProduct);

        if(this.totalRecords == 0 && this.originDateOfLastSatelliteProduct)
        {
          this.resetDateRangeToDefault();
          this.loadProductsList();
        }

        // отметка об обновлении
        this.lastUpdatedAt = this.originDateOfLastSatelliteProduct ?? null;
        this.showUpdatedBanner = true;

        this.satelliteImages = (productsArray || []).map((product: SatelliteProductDto, index: number) => {
          const apiProductId = product.id;
          const previewImagePathFromProduct = product.previewImagePath;
          const bounds = product.bounds;

          // Обработка regionLevel3Ids - API-клиент может ещё не знать о переименовании поля на бэкенде,
          // поэтому проверяем и новое, и старое имя через доступ по строковому ключу.
          const productAny = product as unknown as Record<string, unknown>;
          const regionLevel3Ids = (productAny['regionLevel3Ids'] ?? productAny['regionLevel2Ids'] ?? []) as unknown[];
          const areas = Array.isArray(regionLevel3Ids) ? regionLevel3Ids.map((id: any) => String(id)) : [];

          // Логируем для отладки, если области пустые, но продукт есть
          if (areas.length === 0 && apiProductId) {
            console.warn('⚠️ Продукт без областей:', apiProductId, 'regionLevel3Ids:', regionLevel3Ids);
          }

          return {
            id: offset + index + 1,
            name: product.name || product.fileName || 'Unknown',
            mission: product.mission || 'SENTINEL-2',
            instrument: product.instrument || 'MSI',
            createAt: product.createAt,
            originDate: product.originDate,
            size: product.sizeMb || 0,
            unit: 'MB',
            areas: areas, // Строковые ID регионов уровня 3 (районы)
            type: 'ndvi',
            tag: 'vegetation',
            thumbnail: previewImagePathFromProduct
              ? this.getFileUrl(previewImagePathFromProduct) || 'assets/icons/main/light/Group 6.svg'
              : 'assets/icons/main/light/Group 6.svg',
            apiProductId: apiProductId,
            productId: product.productId,
            sentinelInspireMetadataId: product.sentinelInspireMetadataId,
            previewImagePath: previewImagePathFromProduct,
            mediadataPath: fileIdOrUndefined(product.mediadataPath), // Archive id for download
            tags: product.tags || [],
            isPublic: false ,//product['isPublic'], // Добавляем поле isPublic из API или false по умолчанию
            bounds: bounds,
            center: product.center, // Используем center с бэка
            cloudCover: normalizeCloudCover(product.cloudCover),
            territoryCloudCover: readTerritoryCloudCover(product),
            polygon: product.polygonJson
          } as any;
        });

        // Скрываем баннер через 3 секунды
        setTimeout(() => {
          this.showUpdatedBanner = false;
        }, 3000);
      },
      error: (error) => {
        console.error('❌ Error loading products:', error);
        this.isLoadingProducts = false; // Выключаем лоадер при ошибке
        this.totalRecords = 0;
        this.totalPages = 0;
        this.satelliteImages = [];
        this.showUpdatedBanner = false;
      },
    });
  }

  /**
   * Прямой URL на файл превью (без XHR). Это важно: <img src> не требует CORS,
   * а загрузка blob через HttpClient часто ломается из‑за отсутствия CORS заголовков.
   */
  private getFileUrl(fileId: unknown): string | null {
    if (!hasFileId(fileId)) return null;
    return `${environment.api}/common/FileStorageItem/GetFile/${fileId}`;
  }

  private ensureIndexManagerPatchedForClipping(): void {
    if (this.isIndexManagerPatched) return;
    const indexManager = this.openstreetmapComponent?.indexManager as any;
    if (!indexManager) return;

    const original = indexManager.addIndexTileLayer?.bind(indexManager);
    if (!original) return;
    this.originalIndexManagerAddIndexTileLayer = original;

    indexManager.addIndexTileLayer = (
      indexType: string,
      date: Date,
      opacity: number = 80,
      onLoadingChange?: (loading: boolean) => void,
    ) => {
      const hasPolygon =
        !!this.currentSatellitePolygonLeafletCoords && this.currentSatellitePolygonLeafletCoords.length >= 3;

      // Если выбран снимок и есть контур — показываем индексный слой ТОЛЬКО внутри контура
      if (hasPolygon) {
        onLoadingChange?.(true);

        const obs = indexManager.loadAvailableIndices?.(date, indexType);
        if (!obs || typeof obs.subscribe !== 'function') {
          onLoadingChange?.(false);
          return original(indexType, date, opacity, onLoadingChange);
        }

        obs.subscribe({
          next: (layers: any[]) => {
            const layer = (layers || [])[0];
            const geoServerName = layer?.geoServerName;
            if (geoServerName && this.currentSatellitePolygonLeafletCoords) {
              this.addClippedTileLayer(geoServerName, this.currentSatellitePolygonLeafletCoords, opacity);
            } else {
              this.removeClippedTileLayer();
              original(indexType, date, opacity, onLoadingChange);
            }
          },
          error: () => {
            this.removeClippedTileLayer();
            original(indexType, date, opacity, onLoadingChange);
            onLoadingChange?.(false);
          },
          complete: () => {
            onLoadingChange?.(false);
          },
        });

        return;
      }

      // Если контура нет — обычный глобальный слой
      this.removeClippedTileLayer();
      return original(indexType, date, opacity, onLoadingChange);
    };

    this.isIndexManagerPatched = true;
  }

  /** Клик по снимку: выставить на карте дату индексов = дате снимка */
  private syncMapIndexDateToImage(image: any): void {
    const date = image.originDate;
    if (!date || !this.openstreetmapComponent) return;

    this.ensureIndexManagerPatchedForClipping();

    // Обновляем дату глобального индексного слоя на карте
    (this.openstreetmapComponent as any).selectedIndexDate = date;

    // Перерисовать индексный слой, если он выбран в UI карты
    try {
      (this.openstreetmapComponent as any).onIndexDateChange?.();
    } catch {}
  }

  private removeClippedTileLayer(): void {
    try {
      const map = this.openstreetmapComponent?.getMapInstance?.();
      if (this.clippedTileLayer && map) {
        map.removeLayer(this.clippedTileLayer);
      }
    } catch {}
    this.clippedTileLayer = null;
  }

  private addClippedTileLayer(
    geoServerName: string,
    polygonLatLng: Array<[number, number]>,
    opacityPercent: number,
  ): void {
    const map = this.openstreetmapComponent?.getMapInstance?.();
    if (!map || !(window as any).L) return;

    this.removeClippedTileLayer();

    const L = (window as any).L;
    const baseUrl = `${environment.api}/tiles/Map/GetTile`;

    const ClippedGridLayer = L.GridLayer.extend({
      createTile: function (coords: any, done: any) {
        const tileSize = this.getTileSize();
        const canvas = document.createElement('canvas');
        canvas.width = tileSize.x;
        canvas.height = tileSize.y;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          done(null, canvas);
          return canvas;
        }

        const zoom = coords.z;
        const x = coords.x;
        const y = coords.y;
        const n = Math.PI - (2 * Math.PI * (y + 0.5)) / Math.pow(2, zoom);
        const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
        const lon = ((x + 0.5) / Math.pow(2, zoom)) * 360 - 180;

        const url = `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=${encodeURIComponent(geoServerName)}&coordinateSystem=0`;

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const tileTopLeft = L.point(coords.x * tileSize.x, coords.y * tileSize.y);

            ctx.save();
            ctx.beginPath();
            polygonLatLng.forEach(([plat, plng], idx) => {
              const p = map.project(L.latLng(plat, plng), zoom).subtract(tileTopLeft);
              if (idx === 0) ctx.moveTo(p.x, p.y);
              else ctx.lineTo(p.x, p.y);
            });
            ctx.closePath();
            ctx.clip();

            ctx.drawImage(img, 0, 0, tileSize.x, tileSize.y);
            ctx.restore();
          } catch {
            try {
              ctx.drawImage(img, 0, 0, tileSize.x, tileSize.y);
            } catch {}
          }
          done(null, canvas);
        };

        img.onerror = () => done(null, canvas);
        img.src = url;
        return canvas;
      },
    });

    this.clippedTileLayer = new ClippedGridLayer({
      opacity: opacityPercent / 100,
      maxZoom: 30,
      minZoom: 1,
      className: 'index-tile-layer index-tile-layer--clipped',
    });

    this.clippedTileLayer.addTo(map);
  }

  /**
   * Индексы продукта (для рендера в карточке)
   */
  getProductIndices(productId: string): string[] {
    if (!productId) return [];
    return this.productAvailableIndices[productId] || [];
  }

  getProductIndexOptions(productId: string): Array<{ label: string; value: string }> {
    const available = this.getProductIndices(productId);
    const noneLabel = this.translate.instant('NOT_INDEX_SELECTED') || 'Not selected';
    return [{ label: noneLabel, value: 'none' }, ...available.map((k) => ({ label: this.getIndexLabel(k), value: k }))];
  }

  onProductIndexModelChange(productId: string, indexKey: string): void {
    if (!productId) return;
    this.selectedProductIndex[productId] = indexKey;
    this.applyProductIndexToMap(productId, indexKey);
  }

  // ===== ЛОГИКА ЗОН/КЕША ДЛЯ КАРТЫ (как в upload-images) =====

  /** Сбрасываем кеш продуктов (при смене фильтров и т.п.) */
  private clearProductsCache(): void {
    this.loadedZones = [];
    this.cachedProducts.clear();
  }

  /** Проверка, полностью ли зона покрывает текущие bounds */
  private zoneCoversBounds(
    zone: { north: number; south: number; east: number; west: number },
    bounds: { north: number; south: number; east: number; west: number },
  ): boolean {
    return (
      zone.north >= bounds.north && zone.south <= bounds.south && zone.east >= bounds.east && zone.west <= bounds.west
    );
  }

  /** Пересекаются ли два прямоугольника */
  private boundsIntersect(
    a: { north: number; south: number; east: number; west: number },
    b: { north: number; south: number; east: number; west: number },
  ): boolean {
    return !(a.west > b.east || a.east < b.west || a.north < b.south || a.south > b.north);
  }

  /** Определяем новые зоны для загрузки по текущим bounds */
  private getNewZonesToLoad(currentBounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): Array<{ north: number; south: number; east: number; west: number }> | null {
    let fullyCovered = false;

    for (const zone of this.loadedZones) {
      if (this.zoneCoversBounds(zone.bounds, currentBounds)) {
        fullyCovered = true;
        break;
      }
    }

    if (fullyCovered) {
      return null;
    }

    const hasIntersection = this.loadedZones.some((zone) => this.boundsIntersect(zone.bounds, currentBounds));

    if (!hasIntersection) {
      return [currentBounds];
    }

    // Есть пересечение, но нет полного покрытия — загружаем текущую область как новую зону
    return [currentBounds];
  }

  /** Объединяем новые продукты с кешем (по apiProductId) */
  private mergeProductsWithCache(newProducts: any[]): void {
    if (!newProducts || newProducts.length === 0) {
      return;
    }

    for (const product of newProducts) {
      const key =
        (product.apiProductId as string) ||
        (product.productId as string) ||
        (product.id != null ? String(product.id) : '');

      if (!key) {
        continue;
      }

      this.cachedProducts.set(key, product);
    }
  }

  /** Обновляем список снимков из кеша без запроса на сервер */
  private updateListFromCache(currentBounds: { north: number; south: number; east: number; west: number }): void {
    // Фильтрация уже делается на бэкенде через regionId, tagIds, startDate, endDate
    // Пагинация также делается на бэкенде через offset и take
    // Не нужно дублировать фильтрацию и пагинацию на фронте

    // Убрано: маркеры и кластеры больше не отображаются на карте
    // Остаются только контуры снимков при выборе продукта и границы регионов

    this.isLoadingProducts = false;
    this.lastUpdatedAt = this.originDateOfLastSatelliteProduct ?? null;
    this.showUpdatedBanner = true;
    setTimeout(() => {
      this.showUpdatedBanner = false;
    }, 3000);
  }

  /** Запрос продуктов для конкретных bounds (без пагинации, с кешем) */
  private loadProductsForBounds(
    bounds: { north: number; south: number; east: number; west: number },
    currentBounds: { north: number; south: number; east: number; west: number },
  ): void {
    this.isLoadingProducts = true;

    // Безопасное преобразование дат (без учета часового пояса, чтобы избежать скачков даты)
    const startDateStr = this.selectedStartDate
      ? this.formatDateForQuery(
          this.selectedStartDate instanceof Date ? this.selectedStartDate : new Date(this.selectedStartDate),
        )
      : undefined;

    const endDateStr = this.selectedEndDate
      ? this.formatDateForQuery(
          this.selectedEndDate instanceof Date ? this.selectedEndDate : new Date(this.selectedEndDate),
        )
      : undefined;

    // Маппинг источника данных на тип спутника
    let satelliteType: number | undefined;
    switch (this.selectedDataSource) {
      case 'sentinel':
        satelliteType = 1;
        break;
      case 'landsat':
        satelliteType = 2;
        break;
      case 'all':
      default:
        satelliteType = undefined;
        break;
    }
  }

  loadRegions(): void {
    // Как в GIS: сначала пробуем взять регионы/карты из кеша (IndexedDB) через RegionManagementManager,
    // чтобы дропдаун открывался мгновенно даже после перезагрузки.
    const regions$ = this.getRegionTreeWithCache();
    const tags$ = this.tagService.getVisibleTags();

    forkJoin({ regions: regions$, tags: tags$ }).subscribe({
      next: ({ regions, tags }) => {
        const regionNodes = regions.map((region) => ({
          ...region,
          expanded: false,
          children: region.children
            ? region.children.map((child) => ({
                ...child,
                expanded: false,
                children: child.children ?? [],
              }))
            : [],
        }));

        // Сортируем регионы по названию в текущем языке
        const sortedRegionNodes = this.sortRegionsByLocalizedName(regionNodes);

        const allOption: RegionDictionaryNode = {
          id: 'all',
          name: this.getAllRegionsLabel(),
          parentId: null,
          level: 0,
          regionMapId: null,
          expanded: false,
          children: [],
        };

        this.regionTree = [allOption, ...sortedRegionNodes];
        this.buildRegionIndex(this.regionTree);

        // Дополнительно загружаем все регионы уровня 3 (районы) для корректного отображения областей в карточках
        // Это гарантирует, что все регионы из RegionLevel3Ids будут найдены
        this.regionDictionaryService.getRegionsByLevel(3).subscribe({
          next: (level3Regions: RegionDictionaryNode[]) => {
            level3Regions.forEach((region) => {
              // Добавляем регионы уровня 3 в regionIndex, если их там еще нет
              if (!this.regionIndex.has(region.id)) {
                this.regionIndex.set(region.id, region);
              }
            });
          },
          error: (error) => {
            console.warn('Ошибка загрузки регионов уровня 3 для отображения областей:', error);
          },
        });

        this.selectedRegionLabel = this.getAllRegionsLabel();

        this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);

        // Обновляем tagOptions из tags
        const allLabel = this.translate.instant('ALL_TAGS') || 'Все теги';
        this.tagOptions = [
          { label: allLabel, value: '' },
          ...tags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          })),
        ];

        // Обновляем regionsById для обратной совместимости
        this.regionsById.clear();
        const flattened = this.flattenRegionTree(this.regionTree);
        flattened.forEach((node) => {
          if (node.id !== 'all') {
            this.regionsById.set(node.id, node);
          }
        });

        // Обновляем regionOptions для обратной совместимости
        this.regionOptions = this.regionTree.map(
          (node) =>
            ({
              id: node.id,
              label: this.getLocalizedRegionName(node),
              value: node.id,
              names: node.names,
              children: node.children?.map((child) => ({
                id: child.id,
                label: this.getLocalizedRegionName(child),
                value: child.id,
                names: child.names,
              })),
              regionMapId: node.regionMapId,
              level: node.level,
              hasChildren: (node.children?.length ?? 0) > 0,
            }) as any,
        );
      },
      error: (error) => {
        console.error('Error loading regions:', error);
        this.regionTree = [];
        this.filteredRegionTree = [];
        this.tagOptions = [{ label: this.translate.instant('ALL_TAGS') || 'Все теги', value: '' }];
        this.regionOptions = [
          {
            label: this.translate.instant('ALL_REGIONS') || 'All regions',
            value: 'all',
            id: 'all',
          } as any,
        ];
      },
    });
  }

  private getRegionTreeWithCache(): Observable<RegionDictionaryNode[]> {
    return this.regionManagementManager.toPolygon().pipe(
      map((polygons: GisPolygon[]) => {
        const regionById = new Map<string, any>();
        (polygons || []).forEach((p) => {
          const r = (p as any)?.data?.region;
          if (r?.id && !regionById.has(r.id)) {
            regionById.set(r.id, r);
          }
        });

        const allRegions = Array.from(regionById.values());
        const level2 = allRegions.filter((r) => Number(r.level) === 2);
        const level3 = allRegions.filter((r) => Number(r.level) === 3);

        const toNode = (r: any): RegionDictionaryNode => ({
          id: String(r.id),
          name: r?.names?.['ru-RU'] || r?.names?.['ru'] || r?.names?.['en-US'] || r?.name || '—',
          parentId: r?.parentId ? String(r.parentId) : null,
          level: Number(r.level),
          regionMapId: r?.regionMapId ? String(r.regionMapId) : null,
          names: r?.names || {},
          children: [],
        });

        const level2Nodes = level2.map(toNode);
        const level3Nodes = level3.map(toNode);

        // Собираем дерево: области (level2) -> районы (level3)
        const tree = level2Nodes.map((l2) => ({
          ...l2,
          children: level3Nodes.filter((l3) => l3.parentId === l2.id).map((l3) => ({ ...l3 })),
        }));

        // Если по какой-то причине кеш пустой — fallback на старый сервис
        if (!tree.length) {
          throw new Error('Empty region tree from cache');
        }

        return tree;
      }),
      catchError(() => this.regionDictionaryService.getRegionTree()),
    );
  }

  getAllRegionsLabel(): string {
    return this.translate.instant('ALL_REGIONS') || 'All regions';
  }

  private flattenRegionTree(nodes: RegionDictionaryNode[]): RegionDictionaryNode[] {
    const stack = [...nodes];
    const result: RegionDictionaryNode[] = [];

    while (stack.length) {
      const node = stack.pop();
      if (!node || node.id === 'all') {
        continue;
      }
      result.push(node);
      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return result;
  }

  private buildRegionIndex(nodes: RegionDictionaryNode[]): void {
    this.regionIndex.clear();
    const flattened = this.flattenRegionTree(nodes);
    flattened.forEach((node) => this.regionIndex.set(node.id, node));
  }

  /** Stable references for the upload modal inputs (a `.bind(this)` in the template would create a new function on every change detection). */
  readonly localizedRegionNameFn = (node: RegionDictionaryNode | null | undefined): string =>
    this.getLocalizedRegionName(node);
  readonly allRegionsLabelFn = (): string => this.getAllRegionsLabel();

  getLocalizedRegionName(node: RegionDictionaryNode | null | undefined): string {
    if (!node) {
      return '';
    }
    const currentLang = this.translate.currentLang || 'ru-RU';
    const names = node.names || {};
    return names[currentLang] ?? names[currentLang.split('-')[0]] ?? node.name ?? '';
  }

  private sortRegionsByLocalizedName(regions: RegionDictionaryNode[]): RegionDictionaryNode[] {
    return regions
      .map((region) => ({
        ...region,
        children: region.children ? this.sortRegionsByLocalizedName(region.children) : [],
      }))
      .sort((a, b) => {
        const nameA = this.getLocalizedRegionName(a).toLowerCase();
        const nameB = this.getLocalizedRegionName(b).toLowerCase();
        return nameA.localeCompare(nameB, this.translate.currentLang || 'ru');
      });
  }

  private findRegionNodeById(id: string | null | undefined): RegionDictionaryNode | null {
    if (!id) {
      return null;
    }
    return this.regionIndex.get(id) ?? null;
  }

  private buildFilteredRegionTree(searchTerm: string): RegionDictionaryNode[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return this.regionTree.map((region) => ({
        ...region,
        children: region.children ? region.children.map((child) => ({ ...child })) : [],
      }));
    }

    const filtered: RegionDictionaryNode[] = [];

    this.regionTree.forEach((region) => {
      if (region.id === 'all') {
        if (this.getLocalizedRegionName(region).toLowerCase().includes(term)) {
          filtered.push({ ...region, children: [] });
        }
        return;
      }

      const matchesRegion = this.getLocalizedRegionName(region).toLowerCase().includes(term);
      const matchingChildren =
        region.children
          ?.filter((child) => this.getLocalizedRegionName(child).toLowerCase().includes(term))
          .map((child) => ({ ...child })) ?? [];

      if (matchesRegion || matchingChildren.length) {
        filtered.push({
          ...region,
          expanded: matchesRegion || matchingChildren.length > 0,
          children: matchesRegion
            ? region.children
              ? region.children.map((child) => ({ ...child }))
              : []
            : matchingChildren,
        });
      }
    });

    return filtered;
  }

  /**
   * Получает названия регионов по их ID
   */
  getIndexLabel(indexKey: string): string {
    const preset = this.indexPresets.find((p) => p.key === indexKey);
    if (preset && preset.key !== 'none') {
      return this.translate.instant(preset.label) || preset.label;
    }
    // Если не найден в пресетах, возвращаем ключ в верхнем регистре
    return indexKey.toUpperCase();
  }

  getRegionNamesByIds(regionIds: string[]): string[] {
    if (!regionIds || regionIds.length === 0) {
      return [];
    }

    const names = Array.from(new Set(regionIds)).map((id) => {
      // Сначала ищем в regionsById
      let region = this.regionsById.get(String(id));

      // Если не найдено, ищем в regionIndex
      if (!region) {
        region = this.regionIndex.get(String(id)) || null;
      }

      if (region) {
        // Используем getLocalizedRegionName для правильной локализации
        return this.getLocalizedRegionName(region);
      }

      // Если регион не найден, логируем для отладки и возвращаем ID
      console.warn(
        '⚠️ Регион не найден для ID:',
        String(id),
        'regionsById size:',
        this.regionsById.size,
        'regionIndex size:',
        this.regionIndex.size,
      );
      return `Unknown Region (${id})`;
    });

    return Array.from(new Set(names));
  }

  private completeInit(): void {
    setTimeout(() => {
      if (this.openstreetmapComponent && this.openstreetmapComponent.isMapInitialized) {
        // Включаем показ названий регионов по умолчанию (как в ГИС)
        this.mapController$.next({ setShowRegionNames: this.mapOptions.showRegionNames });

        if (this.selectedMapType === 'satellite') {
          // Спутник: устанавливаем мозаики Sentinel с геосервера как базовый слой
          this.mapController$.next({ setBaseMap: 'sentinel' });
          // Добавляем OSM как наложенный слой поверх спутника
          this.addOSMOverlayLayer();
        } else {
          // Схема: только OSM слой
          this.mapController$.next({ setBaseMap: 'osm' });
        }

        // Глобальный индексный слой управляется `OpenStreetMapComponent`
        // (selectedIndexDate/selectedIndexLayer). Здесь ничего не применяем.

        // После инициализации карты один раз подгружаем и рисуем границы регионов
        this.loadRegionBoundaries();

        // При первой загрузке сразу делаем запрос по границам карты
        const mapInstance = this.openstreetmapComponent.getMapInstance();
        if (mapInstance && typeof mapInstance.getBounds === 'function') {
          const bounds = mapInstance.getBounds();
          this.currentMapBounds = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          };
          // УБРАНО: загрузка продуктов при инициализации карты
          // Список загружается при применении фильтров, а не при инициализации карты
        }
      }
    }, 500);
  }

  get isDarkMode(): boolean {
    return this.themeService.isDarkMode();
  }

  loadMapData(): void {
    // Убрано: маркеры больше не используются
    // this.mapMarkers = [];
  }

  /**
   * Обновляет текущие границы карты из Leaflet и перезагружает продукты
   * (упрощённый вариант без сложного кэша зон — как первый шаг).
   */
  onMapMove(_: any): void {
    this.mapRequest.hideMenu();
    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      return;
    }

    const mapInstance = this.openstreetmapComponent.getMapInstance();
    if (!mapInstance || typeof mapInstance.getBounds !== 'function') {
      return;
    }

    const bounds = mapInstance.getBounds();
    if (!bounds) {
      return;
    }

    // Сохраняем границы карты только для отображения контуров снимков
    // НЕ используем их для фильтрации списка продуктов
    this.currentMapBounds = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    };

    // УБРАНО: загрузка списка при движении карты
    // Список должен зависеть только от фильтров, а не от границ карты
  }

  /**
   * Сохраняем текущий зум.
   * НЕ обновляем список продуктов при изменении зума - список зависит только от фильтров.
   */
  onMapZoomChange(zoom: number): void {
    this.mapRequest.hideMenu();
    this.mapZoom = zoom;
    // Обновляем только границы карты для отображения контуров снимков
    this.onMapMove(null);
  }

  /**
   * Загружает и отображает границы регионов уровня 2 синим цветом,
   * по аналогии с GIS / upload-images, но в упрощённом виде.
   */
  private loadRegionBoundaries(): void {
    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      return;
    }

    if (this.regionBoundariesLoaded) {
      return;
    }

    this.regionBoundariesLoaded = true;

    // OpenStreetMapComponent уже имеет слой для полигонов, не нужно инициализировать отдельно

    // Как в GIS: берём уже распарсенные полигоны из кеша RegionManagementManager
    this.regionManagementManager.toPolygon().subscribe({
      next: (polygons: GisPolygon[]) => {
        if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
          return;
        }

        const level2Polygons = (polygons || []).filter(
          (p) => (p as any)?.type === 'region' && Number((p as any)?.data?.level) === 2,
        );

        level2Polygons.forEach((poly: any) => {
          const coords = poly.coordinates as [number, number][];
          if (!coords?.length) return;

          const regionData = poly.data?.region;
          const regionMapId = regionData?.regionMapId ? String(regionData.regionMapId) : null;
          const cacheKey = regionMapId || String(regionData?.id || poly.id);
          this.regionBoundaryCache.set(cacheKey, coords);

          const labelNode: RegionDictionaryNode = {
            id: String(regionData?.id || poly.id),
            name: regionData?.names?.['ru-RU'] || regionData?.name || poly.name || '',
            parentId: regionData?.parentId ? String(regionData.parentId) : null,
            level: 2,
            regionMapId: regionMapId,
            names: regionData?.names || {},
          };
          const label = this.getLocalizedRegionName(labelNode);

          const uniquePolygonId = `region-boundary-${poly.id}`;
          this.mapController$.next({
            addPolygon: {
              coordinates: coords,
              properties: {
                id: uniquePolygonId,
                type: 'region',
                layer: 'boundary',
                color: '#2563EB',
                weight: 2,
                opacity: 0.9,
                fillColor: 'transparent',
                fillOpacity: 0,
                name: poly.name || '',
                tooltip: poly.name ? label : '',
                tooltipOptions: {
                  permanent: false,
                  direction: 'right',
                  offset: [12, 0],
                  className: 'region-boundary-tooltip',
                  sticky: true,
                },
                interactive: true,
                className: 'region-boundary-polygon',
                data: poly.data,
              },
            },
          });
        });
      },
      error: (error) => {
        console.error('Error loading region boundaries for satellite-images:', error);
      },
    });
  }

  goBack(): void {
    this.location.back();
  }

  toggleLayerDropdown(): void {
    this.isLayerDropdownOpen = !this.isLayerDropdownOpen;
  }

  selectLayer(value: string): void {
    this.selectedLayer = value;
    this.isLayerDropdownOpen = false;

    if (this.openstreetmapComponent) {
      this.applySpectralLayer(value);
    }
  }

  onSelectMapType(type: 'satellite' | 'osm'): void {
    this.selectedMapType = type;

    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      console.warn('Карта не инициализирована, откладываем переключение');
      setTimeout(() => this.onSelectMapType(type), 100);
      return;
    }

    if (type === 'satellite') {
      // Спутник: используем мозаики Sentinel с геосервера как базовый слой
      this.mapController$.next({ setBaseMap: 'sentinel' });
      // Добавляем OSM как наложенный слой поверх спутника с небольшой задержкой
      setTimeout(() => {
        this.addOSMOverlayLayer();
        // Глобальный индексный слой (если выбран) останется управляться `OpenStreetMapComponent`
      }, 100);
    } else {
      // Схема: только OSM слой
      this.mapController$.next({ setBaseMap: 'osm' });
      // Удаляем OSM overlay если он был
      this.removeOSMOverlayLayer();
      // Глобальный индексный слой (если выбран) останется управляться `OpenStreetMapComponent`
    }
  }

  // Меню индексов
  toggleIndexMenu(): void {
    this.isIndexMenuOpen = !this.isIndexMenuOpen;
  }
  selectIndexPreset(
    item: { label: string; icon: string; key: string; isSeparator?: boolean },
    apply: boolean = true,
  ): void {
    this.selectedIndexPreset = item;
    this.isIndexMenuOpen = false;

    if (apply && this.openstreetmapComponent && this.openstreetmapComponent.isMapInitialized) {
      try {
        // TODO: Когда API будет готов, здесь можно добавить запрос для применения индекса к продукту
        // if (this.selectedProductId && item.key !== 'none') {
        //   this.satelliteProducts.applyIndexToProduct(this.selectedProductId, item.key).subscribe({
        //     next: (result) => {
        //       this.applyIndexToMap(item.key);
        //     },
        //     error: (error) => {
        //       console.error('Ошибка применения индекса:', error);
        //     }
        //   });
        // } else {
        //   this.applyIndexToMap(item.key);
        // }

        // Пока используем прямое применение к карте
        this.applyIndexToMap(item.key);
      } catch (error) {
        console.error('Ошибка в selectIndexPreset:', error);
      }
    }
  }

  /**
   * Обработчик изменения индекса или даты (как в ГИС)
   */
  // onIndexDateChange теперь обрабатывается встроенным компонентом карты (OpenStreetMapComponent)

  /**
   * Применяет индекс к карте поверх базового слоя
   */
  private applyIndexToMap(indexKey: string): void {

    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      console.warn('❌ openstreetmapComponent недоступен в applyIndexToMap');
      return;
    }

    // Используем IndexManager из openstreetmapComponent
    const indexManager = this.openstreetmapComponent.indexManager;
    if (!indexManager) {
      console.warn('❌ IndexManager недоступен');
      return;
    }

    if (indexKey === 'none') {
      // Удаляем индексный слой
      indexManager.removeIndexTileLayer();
      return;
    }

    // Получаем продукт для определения даты и geoServerName
    const product = this.satelliteImages.find((img) => img.apiProductId === this.selectedProductId);
    if (!product) {
      console.warn('❌ Продукт не найден для применения индекса');
      return;
    }

    const productDate = product.originDate;

    // Проверяем, есть ли geoServerName для этого индекса в productLayerMap
    const geoServerName = this.productLayerMap[indexKey];
    if (geoServerName) {
      // Используем IndexManager с конкретным geoServerName
      // IndexManager должен поддерживать загрузку по geoServerName
      // Если нет, используем стандартный метод с типом индекса
    }

    // Маппим indexKey в тип индекса для IndexManager
    const indexTypeMap: { [key: string]: string } = {
      ndvi: 'Ndvi',
      gndvi: 'Gndvi',
      mndwi: 'Mndwi',
      ndwi: 'Ndwi',
      ndmi: 'Ndmi',
      evi: 'Evi',
      osavi: 'Osavi',
      arvi: 'Arvi',
      orvi: 'Orvi',
    };

    const indexType = indexTypeMap[indexKey];
    if (!indexType) {
      console.warn('❌ Неизвестный тип индекса:', indexKey);
      return;
    }

    indexManager.addIndexTileLayer(indexType, productDate, this.indexOpacity, (loading: boolean) => {
      // Можно добавить индикатор загрузки если нужно
    });

    // Если выбран спутник, пересоздаем OSM overlay с правильной прозрачностью
    if (this.selectedMapType === 'satellite') {
      this.addOSMOverlayLayer();
    }
  }

  // Меню слоёв
  toggleLayerMenu(): void {
    this.isLayerMenuOpen = !this.isLayerMenuOpen;
  }
  selectLayerPreset(item: { label: string; icon: string; mission: string; key: string }, apply: boolean = true): void {
    this.selectedLayerPreset = item;
    this.isLayerMenuOpen = false;
    if (apply && this.openstreetmapComponent) {
      try {
        switch (item.key) {
          case 'truecolor': // TODO: Заменить setMapLayer на правильный метод через IndexManager или mapController$
          // this.openstreetmapComponent.setMapLayer('truecolor'); break;
          case 'falsecolor': // TODO: Заменить setMapLayer на правильный метод через IndexManager или mapController$
          // this.openstreetmapComponent.setMapLayer('falsecolor'); break;
          case 'swir': // TODO: Заменить setMapLayer на правильный метод через IndexManager или mapController$
          // this.openstreetmapComponent.setMapLayer('swir'); break;
          default:
            break;
        }
      } catch {}
    }
  }

  applySpectralLayer(layerType: string): void {
    switch (layerType) {
      case 'NDVI':
        this.updateMapLayer('NDVI', {
          colorScale: ['#8B4513', '#F4A460', '#FFFF00', '#90EE90', '#228B22'],
          description: 'Normalized Difference Vegetation Index',
          formula: '(B08 - B04) / (B08 + B04)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=NDVI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(0deg) saturate(1.2) contrast(1.1)',
            opacity: 0.8,
          },
        });
        break;
      case 'NDMI':
        this.updateMapLayer('NDMI', {
          colorScale: ['#8B4513', '#DEB887', '#87CEEB', '#4682B4', '#000080'],
          description: 'Normalized Difference Moisture Index',
          formula: '(B08 - B11) / (B08 + B11)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=NDMI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(180deg) saturate(1.3) contrast(1.2)',
            opacity: 0.7,
          },
        });
        break;
      case 'GNDVI':
        this.updateMapLayer('GNDVI', {
          colorScale: ['#F0E68C', '#ADFF2F', '#7CFC00', '#32CD32', '#006400'],
          description: 'Green Normalized Difference Vegetation Index',
          formula: '(B08 - B03) / (B08 + B03)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=GNDVI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(60deg) saturate(1.4) contrast(1.1)',
            opacity: 0.8,
          },
        });
        break;
      case 'OSAVI':
        this.updateMapLayer('OSAVI', {
          colorScale: ['#D2691E', '#F4A460', '#FFFFE0', '#98FB98', '#2E8B57'],
          description: 'Optimized Soil-Adjusted Vegetation Index',
          formula: '(B08 - B04) / (B08 + B04 + 0.16)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=OSAVI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(30deg) saturate(1.1) contrast(1.2)',
            opacity: 0.75,
          },
        });
        break;
      case 'MNDWI':
        this.updateMapLayer('MNDWI', {
          colorScale: ['#8B4513', '#FFDEAD', '#E0FFFF', '#00CED1', '#00008B'],
          description: 'Modified Normalized Difference Water Index',
          formula: '(B03 - B11) / (B03 + B11)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=MNDWI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(200deg) saturate(1.5) contrast(1.3)',
            opacity: 0.8,
          },
        });
        break;
      case 'NDWI':
        this.updateMapLayer('NDWI', {
          colorScale: ['#8B4513', '#FFDEAD', '#E0FFFF', '#00CED1', '#00008B'],
          description: 'Normalized Difference Water Index',
          formula: '(B03 - B08) / (B03 + B08)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=NDWI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(200deg) saturate(1.5) contrast(1.3)',
            opacity: 0.8,
          },
        });
        break;
      case 'EVI':
        this.updateMapLayer('EVI', {
          colorScale: ['#8B0000', '#FF6347', '#FFD700', '#9ACD32', '#006400'],
          description: 'Enhanced Vegetation Index',
          formula: '2.5 * (B08 - B04) / (B08 + 6*B04 - 7.5*B02 + 1)',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=EVI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(320deg) saturate(1.3) contrast(1.2)',
            opacity: 0.8,
          },
        });
        break;
      case 'ORVI':
        this.updateMapLayer('ORVI', {
          colorScale: ['#FFFFE0', '#F0E68C', '#BDB76B', '#6B8E23', '#556B2F'],
          description: 'Optimized Red-edge Vegetation Index',
          formula: 'B08 / B03',
          tileUrl:
            'https://services.sentinel-hub.com/ogc/wms/{instanceId}?SERVICE=WMS&REQUEST=GetMap&LAYERS=ORVI&MAXCC=20&WIDTH=256&HEIGHT=256&FORMAT=image/png&BBOX={bbox}&CRS=EPSG:3857&TIME={time}',
          overlayStyle: {
            filter: 'hue-rotate(45deg) saturate(1.2) contrast(1.1)',
            opacity: 0.75,
          },
        });
        break;
      default:
    }
  }

  updateMapLayer(indexType: string, config: any): void {
    this.currentLayerConfig = {
      type: indexType,
      ...config,
    };

    if (this.openstreetmapComponent) {
      try {
        switch (indexType) {
          case 'NDVI':
            // TODO: Заменить setMapLayer на правильный метод через IndexManager или mapController$
            // this.openstreetmapComponent.setMapLayer('ndvi');
            break;
          default:
            // TODO: Заменить setMapLayer на правильный метод через IndexManager или mapController$
            // this.openstreetmapComponent.setMapLayer('satellite');
            break;
        }

        const mapContainer = document.querySelector('.map-section .leaflet-container');
        if (mapContainer) {
          const overlayStyle = config.overlayStyle || {};

          (mapContainer as HTMLElement).style.filter = '';

          if (overlayStyle.filter) {
            (mapContainer as HTMLElement).style.filter = overlayStyle.filter;
          }

          if (overlayStyle.opacity !== undefined) {
            (mapContainer as HTMLElement).style.opacity = overlayStyle.opacity.toString();
          }
        }

        this.addSpectralOverlay(indexType, config);
      } catch (error) {
        this.applyBasicLayerVisualization(indexType, config);
      }
    }
  }

  private addSpectralOverlay(indexType: string, config: any): void {}

  private applyBasicLayerVisualization(indexType: string, config: any): void {
    const mapContainer = document.querySelector('.map-section .leaflet-container');
    if (mapContainer) {
      const hueRotations: { [key: string]: number } = {
        NDVI: 0,
        NDMI: 180,
        GNDVI: 60,
        OSAVI: 30,
        MNDWI: 200,
        EVI: 320,
        ORVI: 45,
      };

      const hue = hueRotations[indexType] || 0;
      (mapContainer as HTMLElement).style.filter = `hue-rotate(${hue}deg) saturate(1.2) contrast(1.1)`;
      (mapContainer as HTMLElement).style.opacity = '0.8';
    }
  }

  currentLayerConfig: any = null;

  // Убираем методы выбора изображений

  downloadImage(image: any): void {
    if (!hasFileId(image?.mediadataPath)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('SATELLITE_ARCHIVE_NOT_DOWNLOADED'),
        detail: this.translate.instant('SATELLITE_ARCHIVE_NOT_DOWNLOADED_DETAIL'),
      });
      return;
    }

    let fileName = image.name || `satellite_image_${image.apiProductId}`;

    function ensureZipExtension(filename:string) {
      // Регулярное выражение проверяет наличие расширения:
      // точка, за которой следует хотя бы один символ, не являющийся точкой, до конца строки
      const hasExtension = /\.([^.]+)$/.test(filename);
      
      if (hasExtension) {
        return filename;
      } else {
        return filename + '.zip';
      }
    }

    fileName = ensureZipExtension(fileName);

    // Открываем модалку скачивания
    // Создаем новый Subject для отмены текущего скачивания
    this.downloadCancellation$ = new Subject<void>();
    this.showDownloadModal = true;
    this.downloadProgress = 0;
    this.downloadingFileName = fileName;

    // Вызываем метод скачивания файла с отслеживанием прогресса
    this.satelliteProducts
      .getFileWithProgress(image.mediadataPath)
      .pipe(takeUntil(this.downloadCancellation$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              this.downloadProgress = Math.round((event.loaded / event.total) * 100);
            }
          } else if (event.type === HttpEventType.Response) {
            this.downloadProgress = 100;
            const blob = event.body as Blob;

            // Создаем ссылку для скачивания
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;

            // Добавляем ссылку в DOM, кликаем и удаляем
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Освобождаем память
            URL.revokeObjectURL(url);

            // Закрываем модалку через небольшую задержку, чтобы пользователь увидел 100%
            setTimeout(() => {
              this.closeDownloadModal();
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('Скачивание завершено'),
                detail: `${this.translate.instant('Файл скачан')}: ${fileName}`,
              });
            }, 500);

          }
        },
        error: (error) => {
          // Не показываем ошибку, если скачивание было отменено пользователем
          if (error.name !== 'AbortError' && error.message !== 'Aborted') {
            console.error('Error downloading file:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('Ошибка скачивания'),
              detail: this.translate.instant('Не удалось скачать файл'),
            });
          }
          this.closeDownloadModal();
        },
      });
  }

  closeDownloadModal(): void {
    // Отменяем текущее скачивание
    if (this.downloadCancellation$) {
      this.downloadCancellation$.next();
      this.downloadCancellation$.complete();
    }
    this.showDownloadModal = false;
    this.downloadProgress = 0;
    this.downloadingFileName = '';
  }

  viewImageDetails(image: any, event?: Event): void {
    // Модалка просмотра информации о снимке: тянем метаданные и превью через сервис
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!image?.apiProductId) {
      return;
    }

    const apiId = image.apiProductId as string;
    this.infoModalImage = image;
    this.showInfoModal = true;
    this.infoModalLoading = true;
    this.infoModalProduct = null;
    this.infoModalSentinelMetadata = null;
    this.infoModalInspireMetadata = null;
    this.infoModalPreviewUrl = image.thumbnail || null;
    this.infoModalTagNames = (image.tags || []).map((t: any) => t.name).filter((n: any) => !!n);

    // Индексы на этой странице выбираются глобально на карте, поэтому здесь не запрашиваем индексы продукта.

    // 1) Получаем продукт — там могут быть ссылки на метаданные и актуальные пути к preview/mediadata
    this.satelliteProducts.getProductById(apiId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (product) => {
        this.infoModalProduct = product;

        // Обновляем теги из загруженного продукта (если они есть)
        if (product?.['tags'] && Array.isArray(product['tags'])) {
          // Обновляем теги в infoModalImage
          if (this.infoModalImage) {
            this.infoModalImage.tags = product['tags'];
          }
          // Обновляем infoModalTagNames для отображения
          this.infoModalTagNames = product['tags'].map((t: any) => t.name || t).filter((n: any) => !!n);
        }

        // 2) Если есть productId — подтянем Sentinel2 метаданные (для sensing time и др.)
        const productIdForSentinel = product?.productId;
        if (productIdForSentinel) {
          this.satelliteProducts.getSentinel2ById(productIdForSentinel).pipe(takeUntil(this.destroy$)).subscribe({
            next: (s2) => {
              this.infoModalSentinelMetadata = s2;

              // При необходимости обновим время съёмки в карточке, если originDate есть
              const imageIndex = this.satelliteImages.findIndex((img) => img.apiProductId === apiId);
              if (imageIndex > -1 && s2?.originDate) {
                this.satelliteImages[imageIndex].originDate = s2.originDate;
              }
            },
            error: () => {},
          });
        }

        // 3) Inspire-метаданные, если есть идентификатор
        const inspireId = product?.sentinelInspireMetadataId || image?.sentinelInspireMetadataId;
        if (inspireId) {
          this.satelliteProducts.getInspireMetadataById(inspireId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (meta) => {
              this.infoModalInspireMetadata = meta;
            },
            error: () => {},
          });
        }

        // 4) Превью для модалки: используем прямой URL (без XHR blob)
        const previewPath = product?.previewImagePath || image?.previewImagePath;
        if (previewPath) {
          this.infoModalPreviewUrl = this.getFileUrl(previewPath);
        }

        this.infoModalLoading = false;
      },
      error: () => {
        this.infoModalLoading = false;
      },
    });
  }

  /**
   * Обработка клика по элементу списка продуктов
   */
  onProductClick(image: any): void {

    // Второй клик по уже выбранному снимку — снимает выбор
    if (this.selectedProductId && image?.apiProductId && this.selectedProductId === image.apiProductId) {
      if (this.currentSatellitePolygonId) {
        this.mapController$.next({ removePolygon: { id: this.currentSatellitePolygonId } });
      }

      this.mapController$.next({
        removeCustomLayer: { id: this.selectedProductId! }
      })
      this.currentSatellitePolygonId = null;
      this.currentSatellitePolygonLeafletCoords = null;
      this.removeClippedTileLayer();
      this.selectedProductId = null;
      this.activeProductId = null;

      // Перерисовать глобальный индексный слой как обычный (без клиппинга)
      try {
        (this.openstreetmapComponent as any)?.onIndexDateChange?.();
      } catch {}
      return;
    }

    // Если выбран другой продукт, удаляем предыдущий контур спутника
    if (this.selectedProductId && this.selectedProductId !== image.apiProductId) {
      if(this.currentSatellitePolygonId) {
        this.mapController$.next({ removePolygon: { id: this.currentSatellitePolygonId } });
        this.currentSatellitePolygonId = null;
        // Снимаем clipped-тайл прошлого продукта
        this.removeClippedTileLayer();
        this.currentSatellitePolygonLeafletCoords = null;
      }
      
      this.mapController$.next({
        removeCustomLayer: { id: this.selectedProductId! }
      })
    }

    // Обновляем активный продукт и выбранный продукт для индексов
    this.activeProductId = image.apiProductId;
    this.selectedProductId = image.apiProductId;

    // Центрируем карту по bounds продукта
    if (image.bounds) {
      this.centerMapOnBounds(image.bounds);
    } else {
      this.centerMapOnImage(image);
    }

    // Индексы на карточке НЕ используем.
    // По клику: выставляем дату индексов на карте = дате снимка.
    this.syncMapIndexDateToImage(image);

    // Отображаем полигон на карте если он есть
    if (image.polygon) {
      this.displayPolygonOnMap(image.polygon, image.name || 'Satellite Image');
    } else {
      // Если нет полигона, удаляем предыдущий контур спутника
      if (this.currentSatellitePolygonId) {
        this.mapController$.next({ removePolygon: { id: this.currentSatellitePolygonId } });
        this.currentSatellitePolygonId = null;
      }
      this.removeClippedTileLayer();
    }

    this.mapController$.next({
      addCustomLayer: {
        id: image.apiProductId!,
        getUrl: (coords) => {
          const baseUrl = `${environment.api}/tiles/Map/GetTile`;
          const zoom = coords.z;
          const x = coords.x;
          const y = coords.y;
          const n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, zoom);
          const lat = (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
          const lon = (x + 0.5) / Math.pow(2, zoom) * 360 - 180;
          return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=satellite:satellite_product_${this.selectedProductId}&coordinateSystem=0`
        }
      }
    })

  }

  centerMapOnBounds(bounds: any): void {

    if (this.openstreetmapComponent) {
      // Если есть готовый центр с бэкенда, используем его
      if (bounds?.center?.lat && bounds?.center?.lng) {
        this.mapController$.next({ setCenter: { lat: bounds.center.lat, lng: bounds.center.lng, zoom: this.mapZoom } });
      } else {
        // Fallback: вычисляем центр по границам
        const centerLat = (bounds.north + bounds.south) / 2;
        const centerLng = (bounds.east + bounds.west) / 2;
        this.mapController$.next({ setCenter: { lat: centerLat, lng: centerLng, zoom: this.mapZoom } });
      }
      // Используем доступный метод для зума
      this.mapZoom = 8;
    }
  }

  /**
   * Обновляет список индексов на основе доступных
   */
  private updateIndexPresets(): void {
    // Фильтруем indexPresets, оставляя только доступные + "Не выбран"
    const allPresets = [
      { label: this.translate.instant('NOT_INDEX_SELECTED'), icon: 'assets/icons/main/light/Group 6.svg', key: 'none' },
      { label: 'NDVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'ndvi' },
      { label: 'GNDVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'gndvi' },
      { label: 'MNDWI', icon: 'assets/icons/main/light/Group 6.svg', key: 'mndwi' },
      { label: 'NDWI', icon: 'assets/icons/main/light/Group 6.svg', key: 'ndwi' },
      { label: 'NDMI', icon: 'assets/icons/main/light/Group 6.svg', key: 'ndmi' },
      { label: 'EVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'evi' },
      { label: 'OSAVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'osavi' },
      { label: 'ARVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'arvi' },
      { label: 'ORVI', icon: 'assets/icons/main/light/Group 6.svg', key: 'orvi' },
    ];

    if (this.availableIndices.length > 0) {
      this.indexPresets = allPresets.filter(
        (preset) => preset.key === 'none' || this.availableIndices.includes(preset.key),
      );
    } else {
      // Если нет доступных индексов - показываем только "Не выбран" и сообщение
      this.indexPresets = [
        {
          label: this.translate.instant('NOT_INDEX_SELECTED'),
          icon: 'assets/icons/main/light/Group 6.svg',
          key: 'none',
        },
        {
          label: this.translate.instant('NO_LOADED_INDICES'),
          icon: 'assets/icons/main/light/Group 6.svg',
          key: 'no-indices',
          isSeparator: true,
        },
      ];
    }
  }

  /**
   * Обновляет опции дропдауна индексов в модальном окне
   */

  /**
   * Маппинг типа слоя с бэка (LayerInfo.type) в локальный ключ индекса
   */
  private mapLayerTypeToIndexKey(type: string | null | undefined): string | null {
    if (!type) {
      return null;
    }

    const upper = type.toUpperCase();

    switch (upper) {
      case 'NDVI':
        return 'ndvi';
      case 'NDMI':
        return 'ndmi';
      case 'GNDVI':
        return 'gndvi';
      case 'OSAVI':
        return 'osavi';
      case 'MNDWI':
        return 'mndwi';
      case 'NDWI':
        return 'ndwi';
      case 'EVI':
        return 'evi';
      case 'ARVI':
        return 'arvi'; // Добавлен отсутствующий case для ARVI
      case 'ORVI':
        return 'orvi';
      case 'ORIGINAL':
        return 'satellite';
      default:
        return null;
    }
  }

  /**
   * Проверяет выбран ли продукт
   */
  isProductActive(productId: string | undefined): boolean {
    return !!productId && this.selectedProductId === productId;
  }

  /**
   * Переключает состояние dropdown индексов для продукта
   */
  toggleProductIndexDropdown(productId: string | undefined, event?: Event): void {
    if (!productId) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    this.productIndexDropdownOpen[productId] = !this.productIndexDropdownOpen[productId];
  }

  /**
   * Обработчик выбора индекса для продукта
   */
  onProductIndexSelect(productId: string | undefined, indexKey: string, event?: Event): void {
    if (!productId) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }


    this.selectedProductIndex[productId] = indexKey;
    this.productIndexDropdownOpen[productId] = false;

    // Применяем индекс к карте (КЛИППИНГ по контуру выбранного продукта)
    this.applyProductIndexToMap(productId, indexKey);
  }

  /**
   * Применить индекс ТОЛЬКО внутри полигона выбранного продукта
   * (не влияет на глобальный индексный слой карты)
   */
  private applyProductIndexToMap(productId: string, indexKey: string): void {
    // Индексы по продукту на этой странице больше не используем (только глобально на карте).
    // Оставлено как безопасный no-op на случай старых вызовов.
    return;
  }

  /**
   * Получает метку для выбранного индекса продукта
   */
  getProductIndexLabel(productId: string | undefined): string {
    if (!productId) {
      return 'NOT_INDEX_SELECTED';
    }
    const selectedIndex = this.selectedProductIndex[productId] || 'none';
    return this.getIndexLabel(selectedIndex);
  }

  centerMapOnImage(image: any): void {

    // Используем центр напрямую из image.center (приходит с бэкенда)
    if (image?.center?.lat && image?.center?.lng) {

      const centerLat = image.center.lat;
      const centerLng = image.center.lng;


      // Обновляем только если координаты валидные
      if (centerLat && centerLng && !isNaN(centerLat) && !isNaN(centerLng)) {
        this.mapCenter = [centerLat, centerLng];
        this.mapZoom = 8;

        if (this.openstreetmapComponent) {
          this.mapController$.next({ setCenter: { lat: centerLat, lng: centerLng, zoom: this.mapZoom } });
        }

        // Убрано: маркеры больше не используются
        // this.mapMarkers = [...];

      } else {
        this.centerMapOnImageFallback(image);
      }
    } else {
      // Fallback если нет center
      this.centerMapOnImageFallback(image);
    }
  }

  private centerMapOnImageFallback(image: any): void {
    
    const lat = environment.map.lat;
    const lng = environment.map.lng;

    this.mapCenter = [lat, lng];
    this.mapZoom = 8;

    if (this.openstreetmapComponent) {
      this.mapController$.next({ setCenter: { lat, lng, zoom: this.mapZoom } });
    }

    // Убрано: маркеры больше не используются
    // this.mapMarkers = [...];
  }

  /**
   * Отображает полигон снимка на карте
   */
  // ID последнего добавленного контура спутника для удаления только его, а не всех полигонов
  private currentSatellitePolygonId: string | number | null = null;

  displayPolygonOnMap(polygon: any, label: string): void {

    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized || !polygon) {
      console.warn('❌ openstreetmapComponent не инициализирован или polygon недоступен');
      // Если карта еще не инициализирована, откладываем отображение
      if (this.openstreetmapComponent && !this.openstreetmapComponent.isMapInitialized) {
        setTimeout(() => this.displayPolygonOnMap(polygon, label), 200);
      }
      return;
    }

    try {
      // Удаляем только предыдущий контур спутника, если он есть (не трогаем контуры регионов)
      if (this.currentSatellitePolygonId !== null) {
        this.mapController$.next({ removePolygon: { id: this.currentSatellitePolygonId } });
        this.currentSatellitePolygonId = null;
      }

      // Проверяем формат данных
      if (typeof polygon !== 'string' && !Array.isArray(polygon)) {
        console.warn('❌ Неподдерживаемый формат полигона:', typeof polygon);
        return;
      }

      // Используем общий сервис разбора полигонов, как для границ регионов,
      // чтобы корректно обрабатывать WKT/GeoJSON/MultiPolygon и порядок координат
      const parsed = this.polygonService.parsePolygonData(polygon);

      if (!parsed || parsed.length === 0) {
        console.warn('❌ Не удалось распарсить полигон. Исходные данные:', polygon);
        return;
      }

      // Проверяем порядок координат (должны быть [lng, lat])
      const firstPoint = parsed[0];
      if (firstPoint && Array.isArray(firstPoint) && firstPoint.length >= 2) {
        const [firstCoord, secondCoord] = firstPoint;
        // Проверяем, что это действительно lng/lat (lng обычно в диапазоне -180..180, lat -90..90)
        if (Math.abs(firstCoord) > 180 || Math.abs(secondCoord) > 90) {
          console.warn(
            '⚠️ Возможно неправильный порядок координат! Первая координата:',
            firstCoord,
            'Вторая:',
            secondCoord,
          );
        }
      }

      const valid = this.polygonService.validateCoordinates(parsed);

      if (!valid || valid.length === 0) {
        console.warn('❌ Нет валидных координат после валидации');
        return;
      }

      const coords = this.polygonService.toLeafletCoords(valid);

      if (!coords || coords.length === 0) {
        console.warn('❌ Нет валидных координат для отображения');
        return;
      }

      // Проверяем, что координаты в правильном формате для Leaflet [lat, lng]
      const firstLeafletPoint = coords[0];
      if (firstLeafletPoint && Array.isArray(firstLeafletPoint) && firstLeafletPoint.length >= 2) {
        const [lat, lng] = firstLeafletPoint;
        if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
          console.error('❌ ОШИБКА: Неправильный порядок координат для Leaflet! Lat:', lat, 'Lng:', lng);
          return;
        }
      }

      // Генерируем уникальный ID для контура спутника
      const satellitePolygonId = `satellite-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Добавляем полигон с небольшой задержкой после удаления предыдущего
      setTimeout(() => {
        // Используем те же стили, что и в ГИС (LeafletMapComponent.highlightRegionBoundary)
        const polygonOptions = {
          id: satellitePolygonId, // Уникальный ID для возможности удаления
          type: 'satellite', // Тип для идентификации
          layer: 'satellite', // Слой для идентификации
          className: 'satellite-image-polygon',
          interactive: false, // Отключаем интерактивность, чтобы не блокировать перемещение карты
          tooltip: label,
          strokeColor: '#FF5722', // Оранжево-красный цвет контура (как в ГИС)
          strokeWidth: 4, // Толщина линии (как в ГИС)
          opacity: 1, // Полная непрозрачность контура
          fillColor: 'transparent', // Прозрачная заливка
          fillOpacity: 0, // Без заливки
          fill: false, // Отключаем заливку
          dashArray: '10, 5', // Пунктирная линия (как в ГИС)
        };

        this.mapController$.next({
          addPolygon: {
            coordinates: coords,
            properties: polygonOptions,
          },
        });

        // Сохраняем ID для последующего удаления
        this.currentSatellitePolygonId = satellitePolygonId;
        // Сохраняем координаты для клиппинга тайла индекса внутри контура
        this.currentSatellitePolygonLeafletCoords = coords as any;

        // Если глобальный индексный слой выбран на карте — пересоздаём его как clipped внутри контура
        this.ensureIndexManagerPatchedForClipping();
        try {
          (this.openstreetmapComponent as any)?.onIndexDateChange?.();
        } catch {}

      }, 100);
    } catch (error) {
      console.error('❌ Ошибка отображения полигона на карте:', error);
    }
  }

  editImage(image: any): void {
    this.centerMapOnImage(image);
  }

  shareImage(): void {}

  downloadAllImages(): void {}

  applyFilters(): void {
    // Валидация диапазона дат
    if (this.selectedStartDate && this.selectedEndDate && this.selectedStartDate > this.selectedEndDate) {
      const tmp = this.selectedStartDate;
      this.selectedStartDate = this.selectedEndDate;
      this.selectedEndDate = tmp;
    }
    if (this.activeTab === 'download') {
      this.searchExternalScenes();
      return;
    }
    // Сбрасываем на первую страницу и загружаем список на основе фильтров
    this.currentPage = 1;
    this.clearProductsCache();
    this.loadProductsList();
  }

  /** Starts a read-only search of external provider scenes with the current page filters (`download` tab). */
  private searchExternalScenes(): void {
    const providerByDataSource: Record<string, ExternalSceneProviderFilter> = {
      sentinel: 'Sentinel2',
      landsat: 'Landsat',
    };
    this.externalScenes.search({
      startDate: this.selectedStartDate,
      endDate: this.selectedEndDate,
      provider: providerByDataSource[this.selectedDataSource] ?? 'both',
      maxCloudCover: this.selectedMaxCloudCover,
      drawnGeoJson: this.currentDrawnGeoJson(),
      selectedRegionId: this.selectedRegionId || this.selectedRegion || this.selectedParentRegion || null,
      mapBounds: this.captureMapBounds(),
    });
  }

  /** Reads the visible map extent at the moment "Apply" is pressed and keeps it as the last known one. */
  private captureMapBounds(): { north: number; south: number; east: number; west: number } | null {
    const mapInstance = this.openstreetmapComponent?.isMapInitialized ? this.openstreetmapComponent.getMapInstance() : null;
    this.currentMapBounds = readMapBounds(mapInstance, this.currentMapBounds);
    return this.currentMapBounds;
  }

  /** GeoJSON of the shape drawn with the map toolbar, or null when none is drawn or it was removed from the map. */
  private currentDrawnGeoJson(): string | null {
    return drawnShapeToGeoJson(this.lastDrawnShape);
  }

  /** Remembers the shape finished with the map's drawing toolbar (the last one wins) and forgets it once removed. */
  onDrawingCompleted(event: { success: boolean; shape?: DrawnShapeLike }): void {
    if (!event.success || !event.shape) {
      return;
    }
    const shape = event.shape;
    const isTracked = watchDrawnShapeRemoval(shape, () => {
      if (this.lastDrawnShape === shape) {
        this.lastDrawnShape = null;
      }
    });
    this.lastDrawnShape = isTracked ? shape : null;
  }

  filterImages(): void {
    // Применяем фильтры и перезагружаем данные по текущей области карты
    this.applyFilters();
  }

  exportImages(): void {
    // Убрана функциональность экспорта выбранных изображений
  }

  resetFilters(): void {
    // Reset all filter values to their defaults
    this.selectedDataSource = 'all';
    this.resetDateRangeToDefault();
    this.selectedRegion = '';
    this.selectedParentRegion = '';
    this.selectedRegionId = null;
    this.selectedMaxCloudCover = 100;
    this.selectedTag = '';
    this.selectedRegionLabel = this.getAllRegionsLabel();
    this.selectedRegionBoundary = null;
    this.showRegionDropdown = false;
    this.regionSearch = '';
    this.expandedRegionIds.clear();
    this.filteredRegionTree = this.buildFilteredRegionTree('');
    this.showDataSourceDropdown = false;
    this.showTagDropdown = false;
    this.tagSearchText = '';
    this.externalScenes.reset();

    // Сбрасываем пагинацию на первую страницу
    this.currentPage = 1;

    // Сбрасываем кеш и загружаем список на основе фильтров (без зависимости от границ карты)
    this.clearProductsCache();
    this.loadProductsList();
  }

  onMaxCloudCoverChange(value: unknown): void {
    this.selectedMaxCloudCover = normalizeMaxCloudCoverInput(value);
  }

  private resetDateRangeToDefault(): void {
    const today = new Date(this.originDateOfLastSatelliteProduct ?? new Date());
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    this.selectedStartDate = weekAgo;
    this.selectedEndDate = today;
  }

  closeExportModal(): void {
    this.showExportModal = false;
  }

  confirmExport(): void {
    this.showExportModal = false;
  }

  get totalResultsText(): string {
    return this.translate.instant('FOUND_RESULTS', { count: this.totalRecords });
  }

  // Методы пагинации
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;

      if (this.activeTab === 'requests') {
        this.loadTaskStatus();
      } else if (this.activeTab === 'satellites') {
        // Всегда загружаем список с сервера на основе фильтров, не из кеша
        this.loadProductsList();
      }
    }
  }

  // Регион: вспомогательные методы (повторяем паттерн из GIS)
  getDisplayRegionLabel(): string {
    return this.selectedRegionLabel || this.getAllRegionsLabel();
  }

  isRegionExpanded(regionId: string): boolean {
    return this.expandedRegionIds.has(regionId);
  }

  toggleRegionDropdown(): void {
    this.showRegionDropdown = !this.showRegionDropdown;
  }

  clearRegionFilter(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedRegion = '';
    this.selectedParentRegion = '';
    this.selectedRegionLabel = this.getAllRegionsLabel();
    this.selectedRegionBoundary = null;
    this.showRegionDropdown = false;
    this.loadProductsList();
  }

  onRegionSelected(id: string | null): void {
    if (!id) {
      this.selectedRegion = '';
      this.selectedParentRegion = '';
      this.selectedRegionLabel = this.getAllRegionsLabel();
      this.selectedRegionBoundary = null;
      this.loadProductsList();
      return;
    }
    const node = this.findRegionNodeById(id);
    if (!node) return;
    const level = node.level ?? (node.parentId ? 3 : 2);
    if (level > 2) {
      // District (level 3)
      const parentNode = node.parentId ? this.findRegionNodeById(node.parentId) : null;
      this.selectedRegion = node.id;
      this.selectedParentRegion = node.parentId ?? '';
      this.selectedRegionLabel = parentNode
        ? `${this.getLocalizedRegionName(parentNode)} / ${this.getLocalizedRegionName(node)}`
        : this.getLocalizedRegionName(node);
      this.loadRegionBoundary(node);
    } else {
      // Parent region (level 2)
      this.selectedRegion = '';
      this.selectedParentRegion = node.id;
      this.selectedRegionLabel = this.getLocalizedRegionName(node);
      this.loadRegionBoundary(node);
    }
    this.loadProductsList();
  }

  onRegionSearchChange(): void {
    this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
  }

  selectRegion(region: RegionDictionaryNode | any, closeDropdown: boolean = true): void {
    if (region.id === 'all') {
      this.selectedRegionLabel = this.getAllRegionsLabel();
      this.selectedParentRegion = '';
      this.selectedRegion = '';
      this.selectedRegionBoundary = null;
      if (closeDropdown) {
        this.showRegionDropdown = false;
      }
      this.loadProductsList();
      return;
    }

    const regionNode = typeof region === 'object' && 'id' in region ? this.findRegionNodeById(region.id) : null;
    if (!regionNode) return;

    this.selectedRegionLabel = this.getLocalizedRegionName(regionNode);
    // Выбран родительский регион (level 2) - используем parentRegionId
    this.selectedParentRegion = regionNode.id;
    this.selectedRegion = ''; // Сбрасываем район
    if (closeDropdown) {
      this.showRegionDropdown = false;
    }
    this.loadRegionBoundary(regionNode);
    this.loadProductsList();
  }

  selectDistrict(parent: RegionDictionaryNode | any, district: RegionDictionaryNode | any, event?: Event): void {
    event?.stopPropagation();

    const parentNode = typeof parent === 'object' && 'id' in parent ? this.findRegionNodeById(parent.id) : null;
    const districtNode = typeof district === 'object' && 'id' in district ? this.findRegionNodeById(district.id) : null;

    if (!parentNode || !districtNode) return;

    this.selectedRegionLabel = `${this.getLocalizedRegionName(parentNode)} / ${this.getLocalizedRegionName(districtNode)}`;
    // Выбран район (level 3) - используем regionId
    this.selectedRegion = districtNode.id;
    this.selectedParentRegion = parentNode.id; // Сохраняем также родительский регион
    this.showRegionDropdown = false;
    this.loadRegionBoundary(districtNode);
    this.loadProductsList();
  }

  toggleRegionExpansion(region: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedRegionIds.has(region.id)) {
      this.expandedRegionIds.delete(region.id);
      region.expanded = false;
    } else {
      this.expandedRegionIds.add(region.id);
      region.expanded = true;
    }
  }

  private loadRegionBoundary(region: RegionDictionaryNode | null): void {
    if (!region?.regionMapId && !region?.id) {
      this.selectedRegionBoundary = null;
      return;
    }

    const cacheKey = region.regionMapId || region.id ? String(region.regionMapId || region.id) : null;
    if (!cacheKey) {
      this.selectedRegionBoundary = null;
      return;
    }

    // 1) Быстро из локального кеша (наполняется при loadRegionBoundaries)
    if (this.regionBoundaryCache.has(cacheKey)) {
      this.selectedRegionBoundary = this.regionBoundaryCache.get(cacheKey)!;
      this.highlightSelectedRegionBoundary(region, this.selectedRegionBoundary);
      return;
    }

    // 2) Из кеша GIS менеджера (IndexedDB -> memory)
    const cached =
      this.regionManagementManager.getCachedRegionPolygon(cacheKey) ||
      this.regionManagementManager.getCachedRegionPolygon(String(region.id));
    if (cached?.polygon?.coordinates?.length) {
      const coords = cached.polygon.coordinates as [number, number][];
      this.regionBoundaryCache.set(cacheKey, coords);
      this.selectedRegionBoundary = coords;
      this.highlightSelectedRegionBoundary(region, coords);
      return;
    }

    this.selectedRegionBoundary = null;
  }

  private highlightSelectedRegionBoundary(region: RegionDictionaryNode, coords: [number, number][]): void {
    try {
      // Удаляем прошлую подсветку
      if (this.regionBoundaryHighlightId) {
        this.mapController$.next({ removePolygon: { id: this.regionBoundaryHighlightId } });
        this.regionBoundaryHighlightId = null;
      }

      const id = `selected-region-${Date.now()}`;
      this.regionBoundaryHighlightId = id;

      const label = this.getLocalizedRegionName(region);
      this.mapController$.next({
        addPolygon: {
          coordinates: coords,
          properties: {
            id,
            type: 'region',
            layer: 'selected-boundary',
            className: 'selected-region-boundary',
            interactive: false,
            tooltip: label,
            strokeColor: '#FF5722',
            strokeWidth: 4,
            opacity: 1,
            fillColor: 'transparent',
            fillOpacity: 0,
            fill: false,
          },
        },
      });
    } catch {}
  }

  toggleDataSourceDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.showDataSourceDropdown = !this.showDataSourceDropdown;
    if (this.showDataSourceDropdown) {
      this.showRegionDropdown = false;
      this.showTagDropdown = false;
    }
  }

  getDisplayDataSourceLabel(): string {
    const selected = this.satelliteOptions.find((option) => option.value === this.selectedDataSource);
    const key = selected?.labelKey || 'ALL_SATELLITES';
    return this.translate.instant(key);
  }

  selectDataSourceOption(option: { labelKey: string; value: string }, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.showDataSourceDropdown = false;
    this.onDataSourceChange(option.value);
  }

  toggleTagDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.showTagDropdown = !this.showTagDropdown;
    if (this.showTagDropdown) {
      this.showRegionDropdown = false;
      this.showDataSourceDropdown = false;
    }
  }

  getDisplayTagLabel(): string {
    if (!this.selectedTag) {
      return this.tagOptions?.[0]?.label || 'Все теги';
    }

    const selected = this.tagOptions.find((tag) => tag.value === this.selectedTag);
    if (selected) {
      return selected.label;
    }

    return this.translate.instant('ALL_TAGS');
  }

  get filteredTagOptions(): Array<{ label: string; value: string }> {
    if (!this.tagSearchText) {
      return this.tagOptions;
    }

    const searchText = this.tagSearchText.toLowerCase();
    return this.tagOptions.filter((option) => option.value === '' || option.label.toLowerCase().includes(searchText));
  }

  selectTagOption(option: { label: string; value: string }, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.showTagDropdown = false;
    this.tagSearchText = '';
    this.onTagChange(option.value || '');
  }

  onTagChange(value: string | null): void {
    this.selectedTag = value || '';
    this.currentPage = 1;
    this.loadProductsList();
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;

    // Обновляем карту после изменения размера
    setTimeout(() => {
      // On the uploaded tab only the uploaded-images map is on screen. The provider map is display:none there and
      // measures 0x0, so re-fitting it would leave it at the maximum zoom when the user comes back.
      if (this.activeTab === 'uploaded') {
        this.uploadedGeoImagesMap?.refreshSize();
        return;
      }

      if (this.openstreetmapComponent) {
        // Обновляем размер карты
        const mapInstance = this.openstreetmapComponent.getMapInstance();
        if (mapInstance) {
          // Обновляем размер карты
          if (typeof mapInstance.invalidateSize === 'function') {
            mapInstance.invalidateSize();
          }

          // Получаем текущие границы и центрируем карту
          if (typeof mapInstance.getBounds === 'function') {
            const bounds = mapInstance.getBounds();
            if (bounds && typeof mapInstance.fitBounds === 'function') {
              mapInstance.fitBounds(bounds);
            }
          }
        }
      }
    }, 300); // Ждем завершения анимации
  }

  onMapClick(event: any): void {
    this.mapRequest.hideMenu();
  }

  /** True while a drawing or measuring mode owns the right-click. */
  private isMapDrawingActive(): boolean {
    const map = this.openstreetmapComponent;
    return (
      !!map &&
      (map.isDrawingRectangle() ||
        map.isDrawingPolygon() ||
        map.isDrawingBoxPolygon() ||
        map.isDrawingCustomPolygon() ||
        map.isDistanceMeasurementMode())
    );
  }

  // Управление зумом теперь через встроенные элементы управления Leaflet (showControls: true)

  onTabChange(tab: DataSourceTab): void {
    if (this.activeTab === 'download' && tab !== 'download') {
      // Leaving the tab drops its results; the view removes the footprints from the map when it is destroyed.
      this.externalScenes.reset();
    }
    this.activeTab = tab;

    // Загружаем соответствующие данные при смене вкладки
    if (tab === 'requests') {
      this.loadTaskStatus();
    } else if (tab === 'satellites') {
      this.loadProductsList();
    } else if (tab === 'uploaded' && !this.uploadedTabVisited) {
      this.uploadedTabVisited = true;
      this.uploadedGeoImages.initialize();
    }

    // The provider map sits hidden behind the uploaded-images map while that tab is open; Leaflet keeps
    // drawing at the size it last saw unless it is told the container is visible again.
    setTimeout(() => {
      const mapInstance = this.openstreetmapComponent?.getMapInstance();
      if (tab !== 'uploaded' && mapInstance && typeof mapInstance.invalidateSize === 'function') {
        mapInstance.invalidateSize();
      }
    }, 0);
  }

  onDataSourceChange(source: string): void {
    this.selectedDataSource = source;
    // Перезагружаем данные при смене источника
    this.currentPage = 1;
    this.loadProductsList();
  }

  onRequestRowClick(request: RequestData): void {
  }

  onRequestViewDetails(request: RequestData): void {
  }

  onTaskRowClick(task: SatelliteTaskStatus): void {
  }

  onTaskViewDetails(task: SatelliteTaskStatus): void {
  }

  // Методы для работы с тегами
  openTagModal(image: any): void {
    this.selectedImageForTags = image;
    this.editingImageIsPublic = image.isPublic || false;
    this.showTagModal = true;
    this.loadImageTags(image.apiProductId);
  }

  closeTagModal(): void {
    this.showTagModal = false;
    this.selectedImageForTags = null;
    this.imageTags = [];
    this.editingImageIsPublic = false;
  }

  saveImageChanges(): void {
    if (!this.selectedImageForTags?.apiProductId || this.savingImageChanges) return;

    const productId = this.selectedImageForTags.apiProductId;
    this.savingImageChanges = true;

    const publicChanged = this.editingImageIsPublic !== (this.selectedImageForTags.isPublic ?? false);

    this.syncTagsForProduct(productId)
      .pipe(
        switchMap(() =>
          publicChanged
            ? this.satelliteProducts.updateProductPublicStatus(productId, this.editingImageIsPublic)
            : of(void 0),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          const imageIndex = this.satelliteImages.findIndex((img) => img.apiProductId === productId);
          if (imageIndex !== -1) {
            this.satelliteImages[imageIndex].isPublic = this.editingImageIsPublic;
          }

          this.refreshProductsList();

          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Success'),
            detail: this.translate.instant('Image settings updated successfully'),
          });

          this.savingImageChanges = false;
          this.closeTagModal();
        },
        error: (error) => {
          this.savingImageChanges = false;

          let errorMessage = this.translate.instant('Failed to update image settings');

          if (error.status === 404) {
            errorMessage = this.translate.instant('Image not found');
          } else if (error.status === 403) {
            errorMessage = this.translate.instant('Access denied');
          } else if (error.status === 500) {
            errorMessage = this.translate.instant('Server error');
          }

          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Error'),
            detail: errorMessage,
          });
        },
      });
  }

  /**
   * Синхронизирует теги продукта согласно выбранным ID из tag-selector
   */
  private syncTagsForProduct(productId: string): Observable<void> {
    const currentTagIds = this.imageTags.map((relation) => relation.tagId);
    const newTagIds = this.editingTagIds.filter((id) => !currentTagIds.includes(id));
    const removedTagIds = currentTagIds.filter((id) => !this.editingTagIds.includes(id));

    const requests: Observable<unknown>[] = [
      ...newTagIds.map((tagId) =>
        this.tagService.createTagRelation({
          tagId,
          ownerId: productId,
          ownerTypeFullName: SATELLITE_PRODUCT_TAG_OWNER_TYPE,
        }),
      ),
      ...removedTagIds.map((tagId) => this.tagService.removeTagFromSatelliteProduct(productId, tagId)),
    ];

    if (!requests.length) {
      return of(void 0);
    }

    return forkJoin(requests).pipe(map(() => void 0));
  }

  loadImageTags(productId: string): void {
    this.tagLoading = true;
    this.tagService.getTagsByOwner(productId, SATELLITE_PRODUCT_TAG_OWNER_TYPE).pipe(takeUntil(this.destroy$)).subscribe({
      next: (tags) => {
        this.imageTags = tags;
        this.editingTagIds = tags.map((relation) => relation.tagId);
        this.tagLoading = false;
      },
      error: () => {
        this.imageTags = [];
        this.tagLoading = false;
      },
    });
  }

  // Загрузка тегов для фильтра
  loadTagsForFilter(): void {
    this.tagService.getVisibleTags().subscribe({
      next: (tags) => {
        const allLabel = this.translate.instant('ALL_TAGS') || 'Все теги';
        this.tagOptions = [
          { label: allLabel, value: '' },
          ...tags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          })),
        ];
      },
      error: (error) => {
        console.error('Error loading tags for filter:', error);
        const allLabel = this.translate.instant('ALL_TAGS') || 'Все теги';
        this.tagOptions = [{ label: allLabel, value: '' }];
      },
    });
  }

  onEditingTagIdsChange(tagIds: string[]): void {
    this.editingTagIds = tagIds || [];
  }

  closeInfoModal(): void {
    this.showInfoModal = false;
    this.infoModalProduct = null;
    this.infoModalSentinelMetadata = null;
    this.infoModalInspireMetadata = null;
    this.infoModalPreviewUrl = null;
    this.infoModalTagNames = [];
  }

  /** The header button uploads geo-images on the uploaded-images tab, and requests a provider scene everywhere else. */
  openUploadModal(): void {
    if (this.activeTab === 'uploaded') {
      this.showGeoImageUploadModal = true;
      return;
    }
    this.mapRequest.openFromHeader();
  }

  onGeoImagesUploaded(): void {
    this.uploadedGeoImages.refreshAfterUpload();
  }

  /**
   * Обработчик изменения прозрачности индекса
   */
  onIndexOpacityChange(value: number): void {
    this.indexOpacity = value;

    // Обновляем прозрачность слоя индекса на карте
    if (this.selectedIndexPreset && this.selectedIndexPreset.key !== 'none') {
      this.updateIndexOpacity();
    }
  }

  /**
   * Обработчик изменения прозрачности карты
   */
  onMapOpacityChange(value: number): void {
    this.mapOpacity = value;

    if (this.selectedMapType === 'satellite') {
      // Для спутника обновляем прозрачность OSM overlay
      this.updateOSMOverlayOpacity();
    } else {
      // Для схемы обновляем прозрачность базового OSM слоя
      this.updateMapOpacity();
    }
  }

  /**
   * Центрирует карту на исходной позиции
   */

  /**
   * Добавляет OSM как наложенный слой поверх базового спутникового слоя
   */
  addOSMOverlayLayer(): void {
    if (this.openstreetmapComponent) {
      // Проверяем, есть ли метод для добавления OSM как наложенного слоя
      if (
        'addOSMOverlay' in this.openstreetmapComponent &&
        typeof this.openstreetmapComponent.addOSMOverlay === 'function'
      ) {
        (this.openstreetmapComponent as any).addOSMOverlay(this.mapOpacity / 100);
      }
    }
  }

  /**
   * Удаляет OSM overlay слой
   */
  removeOSMOverlayLayer(): void {
    if (this.openstreetmapComponent) {
      // Проверяем, есть ли метод для удаления OSM overlay
      if (
        'removeOSMOverlay' in this.openstreetmapComponent &&
        typeof this.openstreetmapComponent.removeOSMOverlay === 'function'
      ) {
        (this.openstreetmapComponent as any).removeOSMOverlay();
      }
    }
  }

  /**
   * Обновляет прозрачность OSM overlay слоя
   */
  updateOSMOverlayOpacity(): void {
    if (this.openstreetmapComponent && this.selectedMapType === 'satellite') {
      // Проверяем, есть ли метод для установки прозрачности OSM overlay
      if (
        'setOSMOverlayOpacity' in this.openstreetmapComponent &&
        typeof this.openstreetmapComponent.setOSMOverlayOpacity === 'function'
      ) {
        (this.openstreetmapComponent as any).setOSMOverlayOpacity(this.mapOpacity / 100);
      }
    }
  }

  /**
   * Обновляет прозрачность базового слоя карты (для схемы)
   */
  updateMapOpacity(): void {
    if (this.openstreetmapComponent && this.selectedMapType === 'osm') {
      // Проверяем, есть ли метод для установки прозрачности базового слоя
      if (
        'setBaseLayerOpacity' in this.openstreetmapComponent &&
        typeof this.openstreetmapComponent.setBaseLayerOpacity === 'function'
      ) {
        (this.openstreetmapComponent as any).setBaseLayerOpacity(this.mapOpacity / 100);
      }
    }
  }

  /**
   * Обновляет прозрачность слоя индекса
   */
  updateIndexOpacity(): void {
    if (this.openstreetmapComponent && this.selectedIndexPreset && this.selectedIndexPreset.key !== 'none') {
      // Проверяем, есть ли метод для установки прозрачности слоя индекса
      if (
        this.openstreetmapComponent?.osmIndexOverlay &&
        typeof this.openstreetmapComponent.osmIndexOverlay.setIndexLayerOpacity === 'function'
      ) {
        this.openstreetmapComponent.osmIndexOverlay.setIndexLayerOpacity(this.indexOpacity);
      }
    }
  }

  testGetProductById(): void {
    const first = (this.satelliteImages || []).find((i) => !!i.apiProductId);
    if (!first || !first.apiProductId) {
      return;
    }
    this.satelliteProducts.getProductById(first.apiProductId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  testSentinel2WithFilter(productId: string): void {
    if (!productId) {
      return;
    }
    const query: any = {
      take: 10,
      offset: 0,
      sortBy: '',
      sort: 0,
      filters: [{ filterField: 'productId', filterValue: productId, filterOperator: 0 }],
    };
    this.satelliteProducts.getSentinel2ByQuery(query).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  testSentinel2Direct(): void {
    const known = (this.satelliteImages || []).find((i: any) => !!i.productId)?.productId;
    if (!known) {
      return;
    }
    const query: any = {
      take: 10,
      offset: 0,
      sortBy: '',
      sort: 0,
      filters: [{ filterField: 'productId', filterValue: known, filterOperator: 0 }],
    };
    this.satelliteProducts.getSentinel2ByQuery(query).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /**
   * Форматирует дату для запроса в формате YYYY-MM-DD без учета часового пояса
   * Это предотвращает скачки даты из-за конвертации в UTC
   */
  private formatDateForQuery(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
